import { formatDate } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  CompanyAdditionalInfoOption,
  CompanyApplianceGuideOption,
  CompanyCheckInOption,
  CompanyCheckOutOption,
  CompanyHouseRuleOption,
  HospitalityAdditionalInfoDisplayEntry,
  HospitalityAdditionalInfoOwnedEntry,
  HospitalityAdditionalInfoSection,
  HospitalityApplianceGuide,
  HospitalityApplianceGuideCompanyEntry,
  HospitalityApplianceGuideCompanyEntryContent,
  HospitalityApplianceGuideCompanyEntryOverrides,
  HospitalityApplianceGuideOwnedEntry,
  HospitalityApplianceGuideSection,
  HospitalityCheckInDetails,
  HospitalityCheckInOwnedEntry,
  HospitalityCheckOutCompanyEntry,
  HospitalityCheckOutDetails,
  HospitalityCheckOutDisplayEntry,
  HospitalityCheckOutOwnedEntry,
  HospitalityChecklist,
  HospitalityCompanyEntry,
  HospitalityCompanyEntryBase,
  HospitalityDetails,
  HospitalityDetailsPayload,
  HospitalityEntryMetadata,
  HospitalityHouseRuleOwnedEntry,
  HospitalityHouseRuleSection,
  HospitalityLink,
  HospitalityLinkDocument,
  HospitalitySectionPayload,
  LegacyHospitalityHouseRules,
  Property,
  TimeRangeSlot,
} from './property-list.models';
import {
  HOSPITALITY_GROUPS as HOSPITALITY_GROUPS_CONFIG,
  HOSPITALITY_LIMITS as HOSPITALITY_LIMITS_CONFIG,
  HOSPITALITY_SECTION_ORDER as HOSPITALITY_SECTION_ORDER_CONFIG,
  USE_GROUPED_HOSP_STEPS,
} from './property-list.constants';
import {
  HOSPITALITY_DIGITAL_KEY_ACCESS_TYPES,
  HospitalityDigitalKey,
  HospitalityDigitalKeyAccessType,
} from '../models/hospitality-digital-key.model';
import {
  mapAddressValidator,
  normalizeMapAddressInput,
  toValidMapAddress,
} from '../utils/validators/map-address.validator';
import { phoneValidator } from '../utils/validators/phone.validator';
import {Company} from '../company/company.models';
import { MapCoordinates } from '../utils/map-url.utils';
import {
  mapLatitudeValidator,
  mapLongitudeValidator,
  toValidLatitude,
  toValidLongitude,
} from '../utils/validators/map-coordinate.validator';
import type { AppMode } from '../models/app-mode.model';

/**
 * Provides hospitality-specific form and data management for the PropertyList component.
 *
 * The mixin keeps the original method signatures intact so the component continues to expose
 * the same API while allowing the heavy hospitality logic to live in a dedicated file.
 */
export abstract class PropertyListHospitalityMixin {
  protected abstract appMode: AppMode;
  protected abstract translate: TranslateService;
  protected abstract fb: FormBuilder;

  protected readonly HOSPITALITY_LIMITS = HOSPITALITY_LIMITS_CONFIG;
  protected readonly HOSPITALITY_SECTION_ORDER: Array<{ key: string; titleKey: string }> =
    HOSPITALITY_SECTION_ORDER_CONFIG;
  protected readonly HOSPITALITY_GROUPS: Array<{ key: string; titleKey: string; sections: string[] }> =
    HOSPITALITY_GROUPS_CONFIG;
  protected readonly useGroupedHospSteps = USE_GROUPED_HOSP_STEPS;

  protected _editStepsCache: Array<{ key: string; titleKey: string }> = [];
  protected _addStepsCache: Array<{ key: string; titleKey: string }> = [];
  protected hospitalityFormIds = new WeakMap<FormGroup, string>();
  protected hospitalityFormCounter = 0;
  protected readonly companyPreviewStateCache = new WeakMap<
    FormGroup,
    Record<'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo', Set<string>>
  >();
  protected readonly propertyPreviewStates = new WeakMap<
    FormGroup,
    {
      houseRules: boolean;
      checkOut: boolean;
      additionalInfo: boolean;
      applianceGuides: Set<number>;
      applianceGuidesOpen: boolean;
    }
  >();

  currentEditStep = 0;
  currentAddStep = 0;
  trackByStep = (_: number, s: { key: string }) => s.key;
  trackByCheckInOptionId = (_: number, option: CompanyCheckInOption) => option.id;

  company: Company | null = null;
  companyHouseRuleOptions: CompanyHouseRuleOption[] = [];
  companyApplianceGuideOptions: CompanyApplianceGuideOption[] = [];
  companyCheckOutOptions: CompanyCheckOutOption[] = [];
  companyCheckInOptions: CompanyCheckInOption[] = [];
  companyAdditionalInfoOptions: CompanyAdditionalInfoOption[] = [];

  readonly digitalKeyAccessTypes = HOSPITALITY_DIGITAL_KEY_ACCESS_TYPES;

  detailsDrawerVisible = false;
  detailsProperty?: Property;


  protected toStepObjects(arr: Array<string | { key: string; titleKey?: string }>): Array<{ key: string; titleKey: string }> {
    return arr.map((x: any) =>
      typeof x === 'string'
        ? { key: x, titleKey: `PROPERTY_LIST.${x.toUpperCase()}` }
        : { key: x.key, titleKey: x.titleKey ?? `PROPERTY_LIST.${x.key.toUpperCase()}` }
    );
  }

  protected recomputeStepCaches(): void {
    if (this.isHospitalityMode()) {
      const groups = this.useGroupedHospSteps
        ? this.HOSPITALITY_GROUPS.map(g => ({ key: g.key, titleKey: g.titleKey }))
        : this.toStepObjects(this.HOSPITALITY_SECTION_ORDER as any);

      const withBasics = [{ key: 'basics', titleKey: 'PROPERTY_LIST.BASICS' }, ...groups];

      this._editStepsCache = withBasics;
      this._addStepsCache  = withBasics;
    } else {
      const steps = [
        { key: 'basics',   titleKey: 'PROPERTY_LIST.BASICS' },
        { key: 'contacts', titleKey: 'PROPERTY_LIST.CONTACTS' },
        { key: 'services', titleKey: 'PROPERTY_LIST.SERVICES' },
      ];
      if (!this.isEmergencyMode()) steps.push({ key: 'hospitality', titleKey: 'PROPERTY_LIST.HOSPITALITY' });
      this._editStepsCache = steps;
      this._addStepsCache = [...steps];
    }

    // keep indices in range
    this.currentEditStep = Math.min(this.currentEditStep ?? 0, Math.max(this._editStepsCache.length - 1, 0));
    this.currentAddStep  = Math.min(this.currentAddStep  ?? 0, Math.max(this._addStepsCache.length  - 1, 0));
  }

  /** Template helper: show a section if this group/section is active */
  showSection(active: string[] | string | undefined, sectionKey: string): boolean {
    if (!active) return true;                                    // show all when no active filter
    return Array.isArray(active) ? active.includes(sectionKey)   // grouped-mode
      : active === sectionKey;        // single-section mode
  }

  /** internal: compute active hosp sections for a given step-index */
  protected activeHospSectionsFor(stepIndex: number, isAddFlow: boolean): string[] | undefined {
    if (!this.isHospitalityMode()) return undefined;

    const steps = isAddFlow ? this.addSteps : this.editSteps;
    const step = steps[stepIndex];

    // Basics should NOT show any hospitality sections
    if (step?.key === 'basics') return [];  // was: undefined

    if (this.useGroupedHospSteps) {
      const groupIndex = stepIndex - 1; // groups start after Basics
      return this.HOSPITALITY_GROUPS[groupIndex]?.sections ?? [];
    } else {
      return step?.key ? [step.key] : [];
    }
  }

  /** expose to template */
  get activeEditHospSections(): string[] | undefined {
    return this.activeHospSectionsFor(this.currentEditStep, /*isAddFlow*/ false);
  }
  get activeAddHospSections(): string[] | undefined {
    return this.activeHospSectionsFor(this.currentAddStep, /*isAddFlow*/ true);
  }

  isEmergencyMode(): boolean { return this.appMode === 'emergency'; }
  isHospitalityMode(): boolean { return this.appMode === 'hospitality'; }

  get editSteps(): Array<{ key: string; titleKey: string }> { return this._editStepsCache; }
  get addSteps():  Array<{ key: string; titleKey: string }> { return this._addStepsCache; }

  get isLastEditStep(): boolean {
    return this.currentEditStep >= this.editSteps.length - 1;
  }
  get isLastAddStep(): boolean {
    return this.currentAddStep >= this.addSteps.length - 1;
  }

  // --- CHANGE: make navigation use dynamic step counts ---
  onEditStepIndexChange(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= this.editSteps.length) return;
    this.currentEditStep = index;
  }

  onAddStepIndexChange(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= this.addSteps.length) return;
    this.currentAddStep = index;
  }

  nextEditStep() { if (this.currentEditStep < this.editSteps.length - 1) this.currentEditStep++; }
  prevEditStep() { if (this.currentEditStep > 0) this.currentEditStep--; }

  nextAddStep() { if (this.currentAddStep < this.addSteps.length - 1) this.currentAddStep++; }
  prevAddStep() { if (this.currentAddStep > 0) this.currentAddStep--; }


  openDetailsDrawer(property: Property) {
    this.detailsProperty = property;
    this.detailsDrawerVisible = true;
  }

  closeDetailsDrawer() {
    this.detailsDrawerVisible = false;
    this.detailsProperty = undefined;
  }

  protected createOwnedEntryGroup<TEntry extends HospitalityEntryMetadata>(
    entry: TEntry | undefined,
    controlsFactory: (entry: TEntry | undefined) => Record<string, any>
  ): FormGroup {
    const controls = controlsFactory(entry);
    return this.fb.group(controls);
  }

  protected parseTimeStringToDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  protected formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  protected normalizeTimeValue(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return undefined;
      }
      return this.formatTime(value);
    }
    if (typeof value === 'string') {
      const cleaned = this.cleanString(value);
      if (!cleaned) {
        return undefined;
      }
      const parsed = this.parseTimeStringToDate(cleaned);
      return parsed ? this.formatTime(parsed) : cleaned;
    }
    return undefined;
  }

  formatHospitalityPreviewTime(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? ''
        : formatDate(value, 'HH:mm', this.translate.currentLang || 'en-US');
    }

    const raw = typeof value === 'string' ? value : String(value);
    const cleaned = this.cleanString(raw);
    if (!cleaned) {
      return '';
    }

    const parsedTime = this.parseTimeStringToDate(cleaned);
    if (parsedTime) {
      return formatDate(parsedTime, 'HH:mm', this.translate.currentLang || 'en-US');
    }

    const parsedDate = new Date(cleaned);
    if (!Number.isNaN(parsedDate.getTime())) {
      return formatDate(parsedDate, 'HH:mm', this.translate.currentLang || 'en-US');
    }

    return cleaned;
  }

  protected formatSupportHourSlot(slot: TimeRangeSlot): string | undefined {
    const start = this.normalizeTimeValue(slot.start);
    const end = this.normalizeTimeValue(slot.end);
    if (start && end) {
      return `${start} - ${end}`;
    }
    if (start || end) {
      return start || end;
    }
    return undefined;
  }

  protected parseSupportHourSlots(value?: string | null): TimeRangeSlot[] {
    if (!value) {
      return [];
    }
    return value
      .split(/[,;]+/)
      .map(segment => segment.trim())
      .filter(segment => segment.length > 0)
      .map(segment => {
        const rangeMatch = segment.match(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/);
        if (rangeMatch) {
          return {
            start: this.parseTimeStringToDate(rangeMatch[1]),
            end: this.parseTimeStringToDate(rangeMatch[2]),
          } as TimeRangeSlot;
        }
        const singleMatch = segment.match(/(\d{1,2}:\d{2})/);
        if (singleMatch) {
          return { start: this.parseTimeStringToDate(singleMatch[1]), end: null } as TimeRangeSlot;
        }
        return { start: null, end: null } as TimeRangeSlot;
      })
      .filter(slot => slot.start || slot.end);
  }

  protected createSupportHourSlotGroup(slot?: TimeRangeSlot): FormGroup {
    return this.fb.group({
      start: [slot?.start ?? null],
      end: [slot?.end ?? null],
    });
  }

  protected initializeHostSupportHourSlots(group: FormGroup, initialValue?: string | null): void {
    const slotsArray = group.get('supportHoursSlots') as FormArray;
    if (!slotsArray) {
      return;
    }
    slotsArray.clear();
    const slots = this.parseSupportHourSlots(initialValue);
    if (slots.length) {
      slots.forEach(slot => slotsArray.push(this.createSupportHourSlotGroup(slot)));
    } else {
      slotsArray.push(this.createSupportHourSlotGroup());
    }
    const control = group.get('supportHours') as FormControl;
    if (slots.length && control) {
      const formatted = slots
        .map(slot => this.formatSupportHourSlot(slot))
        .filter((value): value is string => !!value)
        .join(', ');
      control.setValue(formatted, { emitEvent: false });
    } else if (control) {
      control.setValue(initialValue || '', { emitEvent: false });
    }
    slotsArray.valueChanges.subscribe(() => this.syncHostSupportHourSlots(group));
  }

  protected syncHostSupportHourSlots(group: FormGroup): void {
    const slotsArray = group.get('supportHoursSlots') as FormArray;
    const control = group.get('supportHours') as FormControl;
    if (!slotsArray || !control) {
      return;
    }
    const formatted = slotsArray.controls
      .map(slotControl => this.formatSupportHourSlot(slotControl.value as TimeRangeSlot))
      .filter((value): value is string => !!value)
      .join(', ');
    control.setValue(formatted, { emitEvent: false });
  }

  protected getHostContactGroup(form: FormGroup): FormGroup {
    return this.getHospitalityDetailsForm(form).get('hostContact') as FormGroup;
  }

  protected getHostSupportHoursArray(form: FormGroup): FormArray {
    return this.getHostContactGroup(form).get('supportHoursSlots') as FormArray;
  }

  getHostSupportHourSlots(form: FormGroup): FormArray {
    return this.getHostSupportHoursArray(form);
  }

  hostSupportHourSlotsLength(form: FormGroup): number {
    return this.getHostSupportHoursArray(form)?.length || 0;
  }

  addHostSupportHourSlot(form: FormGroup): void {
    const array = this.getHostSupportHoursArray(form);
    if (array.length >= 2) {
      return;
    }
    array.push(this.createSupportHourSlotGroup());
  }

  removeHostSupportHourSlot(form: FormGroup, index: number): void {
    const array = this.getHostSupportHoursArray(form);
    if (array.length <= 1) {
      return;
    }
    array.removeAt(index);
  }

  onHostSupportHourStartChange(form: FormGroup, index: number): void {
    const array = this.getHostSupportHoursArray(form);
    const slot = array.at(index) as FormGroup;
    const start = slot.get('start')?.value as Date | null;
    const endControl = slot.get('end') as FormControl;
    const end = endControl?.value as Date | null;
    if (start && end && end < start) {
      endControl.setValue(null);
    }
  }

  getDisabledHostSupportHourEndHours(form: FormGroup, index: number): () => number[] {
    return () => {
      const array = this.getHostSupportHoursArray(form);
      const start = (array.at(index) as FormGroup).get('start')?.value as Date | null;
      if (!start) {
        return [];
      }
      const hour = start.getHours();
      return Array.from({ length: hour }, (_, i) => i);
    };
  }

  getDisabledHostSupportHourEndMinutes(form: FormGroup, index: number): (hour: number) => number[] {
    return (hour: number) => {
      const array = this.getHostSupportHoursArray(form);
      const start = (array.at(index) as FormGroup).get('start')?.value as Date | null;
      if (!start) {
        return [];
      }
      if (hour !== start.getHours()) {
        return [];
      }
      const minute = start.getMinutes();
      return Array.from({ length: minute }, (_, i) => i);
    };
  }

  getDisabledHostSupportHourEndSeconds(form: FormGroup, index: number): (hour: number, minute: number) => number[] {
    return (hour: number, minute: number) => {
      const array = this.getHostSupportHoursArray(form);
      const start = (array.at(index) as FormGroup).get('start')?.value as Date | null;
      if (!start) {
        return [];
      }
      if (hour !== start.getHours() || minute !== start.getMinutes()) {
        return [];
      }
      const second = start.getSeconds();
      return Array.from({ length: second }, (_, i) => i);
    };
  }

  onCheckInScheduleStartChange(form: FormGroup): void {
    const scheduleGroup = this.getCheckInOwnedEntry(form).get('schedule') as FormGroup;
    const from = scheduleGroup.get('from')?.value as Date | null;
    const untilControl = scheduleGroup.get('until') as FormControl;
    const until = untilControl?.value as Date | null;
    if (from && until && until < from) {
      untilControl.setValue(null);
    }
  }

  getDisabledCheckInUntilHours(form: FormGroup): () => number[] {
    return () => {
      const scheduleGroup = this.getCheckInOwnedEntry(form).get('schedule') as FormGroup;
      const from = scheduleGroup.get('from')?.value as Date | null;
      if (!from) {
        return [];
      }
      const hour = from.getHours();
      return Array.from({ length: hour }, (_, i) => i);
    };
  }

  getDisabledCheckInUntilMinutes(form: FormGroup): (hour: number) => number[] {
    return (hour: number) => {
      const scheduleGroup = this.getCheckInOwnedEntry(form).get('schedule') as FormGroup;
      const from = scheduleGroup.get('from')?.value as Date | null;
      if (!from) {
        return [];
      }
      if (hour !== from.getHours()) {
        return [];
      }
      const minute = from.getMinutes();
      return Array.from({ length: minute }, (_, i) => i);
    };
  }

  getDisabledCheckInUntilSeconds(form: FormGroup): (hour: number, minute: number) => number[] {
    return (hour: number, minute: number) => {
      const scheduleGroup = this.getCheckInOwnedEntry(form).get('schedule') as FormGroup;
      const from = scheduleGroup.get('from')?.value as Date | null;
      if (!from) {
        return [];
      }
      if (hour !== from.getHours() || minute !== from.getMinutes()) {
        return [];
      }
      const second = from.getSeconds();
      return Array.from({ length: second }, (_, i) => i);
    };
  }

  protected createHouseRuleOwnedEntryGroup(entry?: HospitalityHouseRuleOwnedEntry): FormGroup {
    return this.createOwnedEntryGroup(entry, () => ({
      rule: [
        this.cleanString(entry?.rule)
          || this.cleanString((entry as any)?.title)
          || this.cleanString((entry as any)?.summary)
          || this.cleanString((entry as any)?.details)
          || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.rule)],
      ],
      links: this.fb.array((entry?.links || []).map(link => this.createLinkGroup(link))),
    }));
  }

  protected createCheckInOwnedEntryGroup(entry?: HospitalityCheckInOwnedEntry): FormGroup {
    return this.createOwnedEntryGroup(entry, () => ({
      title: [entry?.title || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.title)]],
      schedule: this.fb.group({
        from: [this.parseTimeStringToDate(entry?.schedule?.from)],
        until: [this.parseTimeStringToDate(entry?.schedule?.until)],
      }),
      instructions: [entry?.instructions || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.instructions)]],
      keyPickupLocation: [
        this.normalizeAddressInput(entry?.keyPickupLocation ?? (entry as any)?.location) ?? '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.location), mapAddressValidator()],
      ],
      contact: [entry?.contact || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.contact)]],
      links: this.fb.array((entry?.links || []).map(link => this.createLinkGroup(link))),
      digitalKeys: this.fb.array((entry?.digitalKeys || []).map(key => this.createDigitalKeyGroup(key))),
    }));
  }

  protected createCheckOutOwnedEntryGroup(entry?: HospitalityCheckOutOwnedEntry): FormGroup {
    const legacySummary = this.cleanString((entry as any)?.summary) || undefined;
    const legacyDetails = this.cleanString((entry as any)?.details) || undefined;
    const legacyDeadline = this.cleanString((entry as any)?.deadline) || undefined;
    const legacyLocation = this.normalizeAddressInput((entry as any)?.location) || undefined;
    return this.createOwnedEntryGroup(entry, () => ({
      title: [entry?.title || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.title)]],
      instructions: [
        entry?.instructions || legacySummary || legacyDetails || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.instructions)],
      ],
      checkOutTime: [this.parseTimeStringToDate(entry?.checkOutTime || legacyDeadline || undefined)],
      dropOffLocation: [
        this.normalizeAddressInput(entry?.dropOffLocation ?? legacyLocation) ?? '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.location), mapAddressValidator()],
      ],
      contact: [entry?.contact || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.contact)]],
      links: this.fb.array((entry?.links || []).map(link => this.createLinkGroup(link))),
      checklist: this.fb.group({
        title: [
          entry?.checklist?.title || '',
          [Validators.maxLength(this.HOSPITALITY_LIMITS.checklistTitle)],
        ],
        items: this.fb.array(
          (entry?.checklist?.items || []).map(item =>
            this.fb.control(item || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.checklistItem)])
          ),
        ),
      }),
    }));
  }

  protected createApplianceGuideOwnedEntryGroup(entry?: HospitalityApplianceGuideOwnedEntry): FormGroup {
    const steps = Array.isArray(entry?.steps) ? entry?.steps : [];
    return this.createOwnedEntryGroup(entry, () => ({
      appliance: [entry?.appliance || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.title)]],
      summary: [entry?.summary || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.summary)]],
      steps: this.fb.array(
        steps.map(step =>
          this.fb.control(step || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.details)])
        ),
      ),
      resources: this.fb.array((entry?.resources || []).map(resource => this.createLinkGroup(resource))),
    }));
  }

  protected createAdditionalInfoOwnedEntryGroup(entry?: HospitalityAdditionalInfoOwnedEntry): FormGroup {
    const resolvedBody =
      typeof entry?.body === 'string'
        ? entry.body
        : typeof (entry as any)?.details === 'string'
          ? (entry as any).details
          : '';
    return this.createOwnedEntryGroup(entry, () => ({
      title: [entry?.title || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.title)]],
      body: [resolvedBody, [Validators.maxLength(this.HOSPITALITY_LIMITS.details)]],
      links: this.fb.array((entry?.links || []).map(link => this.createLinkGroup(link))),
    }));
  }

  protected normalizeLegacyCheckInOwnedEntries(
    checkIn: HospitalityDetails['checkIn']
  ): HospitalityCheckInOwnedEntry[] {
    if (!checkIn) {
      return [];
    }

    const section = checkIn as HospitalitySectionPayload<HospitalityCheckInOwnedEntry>;
    if (Array.isArray(section?.ownedEntries)) {
      return section.ownedEntries;
    }

    const legacy = checkIn as HospitalityCheckInDetails;
    const from = legacy?.from || '';
    const until = legacy?.until || '';
    const instructions = this.cleanString(legacy?.instructions) || '';
    const resolvedLocation = this.normalizeAddressInput((legacy as any)?.keyPickupLocation ?? (legacy as any)?.location) || '';
    const contact = this.cleanString(legacy?.contact) || '';
    const hasSchedule = !!(from || until);
    const title =
      this.cleanString((legacy as any)?.title)
      || instructions
      || (hasSchedule ? `Check-in window ${from || '—'} – ${until || '—'}` : '')
      || resolvedLocation
      || contact
      || 'Check-in information';

    const entry: HospitalityCheckInOwnedEntry = {
      title,
    };
    if (hasSchedule) {
      entry.schedule = {};
      if (from) { entry.schedule.from = from; }
      if (until) { entry.schedule.until = until; }
    }
    if (instructions) { entry.instructions = instructions; }
    if (resolvedLocation) { entry.keyPickupLocation = resolvedLocation; }
    if (contact) { entry.contact = contact; }
    if (Array.isArray(legacy?.links) && legacy.links.length) {
      entry.links = legacy.links;
    }
    if (Array.isArray(legacy?.digitalKeys) && legacy.digitalKeys.length) {
      const digitalKeys = legacy.digitalKeys
        .map(key => this.cleanDigitalKey(key))
        .filter((key): key is HospitalityDigitalKey => !!key);
      if (digitalKeys.length) {
        entry.digitalKeys = digitalKeys;
      }
    }
    return [entry];
  }

  protected isCheckInSectionPayload(
    value: HospitalityDetails['checkIn']
  ): value is HospitalitySectionPayload<HospitalityCheckInOwnedEntry> {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as HospitalitySectionPayload<HospitalityCheckInOwnedEntry>;
    return Array.isArray(candidate?.ownedEntries)
      || Array.isArray(candidate?.companyEntries)
      || Array.isArray(candidate?.companyEntryIds);
  }

  protected normalizeCheckInEntries(details?: HospitalityDetails): {
    ownedEntries: HospitalityCheckInOwnedEntry[];
    companyEntries: HospitalityCompanyEntry[];
  } {
    const checkIn = details?.checkIn;
    if (this.isCheckInSectionPayload(checkIn)) {
      const ownedEntries = Array.isArray(checkIn.ownedEntries) ? checkIn.ownedEntries : [];
      const companyEntries = Array.isArray(checkIn.companyEntries) ? checkIn.companyEntries : [];
      if (!companyEntries.length && Array.isArray(checkIn.companyEntryIds)) {
        const builtEntries = this.buildCompanyEntries<HospitalityCompanyEntry>(checkIn.companyEntryIds);
        return { ownedEntries, companyEntries: builtEntries };
      }
      return { ownedEntries, companyEntries };
    }

    const ownedEntries = this.normalizeLegacyCheckInOwnedEntries(checkIn);
    const legacyCompanyIds = Array.isArray((checkIn as any)?.companyEntryIds)
      ? (checkIn as any).companyEntryIds
      : [];
    const companyEntries = this.buildCompanyEntries<HospitalityCompanyEntry>(legacyCompanyIds);

    return { ownedEntries, companyEntries };
  }

  protected isCheckOutSectionPayload(
    value: HospitalityDetails['checkOut']
  ): value is HospitalitySectionPayload<HospitalityCheckOutOwnedEntry, HospitalityCheckOutCompanyEntry> {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as HospitalitySectionPayload<HospitalityCheckOutOwnedEntry, HospitalityCheckOutCompanyEntry>;
    return Array.isArray(candidate?.ownedEntries)
      || Array.isArray(candidate?.companyEntries)
      || Array.isArray(candidate?.companyEntryIds);
  }

  protected normalizeLegacyCheckOutOwnedEntries(
    checkOut: HospitalityCheckOutDetails | undefined
  ): HospitalityCheckOutOwnedEntry[] {
    if (!checkOut) {
      return [];
    }

    const until = checkOut.until || '';
    const instructions = checkOut.instructions || '';
    const location = checkOut.location || '';
    const contact = checkOut.contact || '';
    const links = Array.isArray(checkOut.links) ? checkOut.links : [];
    const checklistTitle = checkOut.checklist?.title || '';
    const checklistItems = Array.isArray(checkOut.checklist?.items) ? checkOut.checklist!.items : [];
    const hasChecklist = !!(checklistTitle || checklistItems.length);
    const hasContent = instructions || location || contact || until || hasChecklist || links.length;
    if (!hasContent) {
      return [];
    }

    const entry: HospitalityCheckOutOwnedEntry = {
      title:
        this.cleanString((checkOut as any)?.title)
        || instructions
        || location
        || contact
        || until
        || 'Check-out details',
    };

    if (until) {
      entry.checkOutTime = until;
    }
    if (location) {
      entry.dropOffLocation = location;
    }
    if (contact) {
      entry.contact = contact;
    }
    if (instructions) {
      entry.instructions = instructions;
    }
    if (links.length) {
      entry.links = links;
    }
    if (hasChecklist) {
      entry.checklist = {};
      if (checklistTitle) {
        entry.checklist.title = checklistTitle;
      }
      if (checklistItems.length) {
        entry.checklist.items = checklistItems;
      }
    }

    return [entry];
  }

  protected normalizeCheckOutEntries(details?: HospitalityDetails): {
    ownedEntries: HospitalityCheckOutOwnedEntry[];
    companyEntries: HospitalityCheckOutCompanyEntry[];
  } {
    const checkOut = details?.checkOut;
    if (this.isCheckOutSectionPayload(checkOut)) {
      const ownedEntries = Array.isArray(checkOut.ownedEntries) ? checkOut.ownedEntries : [];
      const companyEntries = Array.isArray(checkOut.companyEntries) && checkOut.companyEntries.length
        ? checkOut.companyEntries
        : this.buildCompanyEntries<HospitalityCheckOutCompanyEntry>(checkOut.companyEntryIds);
      return {
        ownedEntries,
        companyEntries,
      };
    }

    const ownedEntries = this.normalizeLegacyCheckOutOwnedEntries(checkOut as HospitalityCheckOutDetails | undefined);
    const companyEntries = this.buildCompanyEntries<HospitalityCheckOutCompanyEntry>(
      details?.selectedCompanyCheckOutItemIds
    );

    return { ownedEntries, companyEntries };
  }

  protected isApplianceGuidesSectionPayload(
    value: HospitalityDetails['applianceGuides']
  ): value is HospitalityApplianceGuideSection {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as HospitalityApplianceGuideSection;
    return Array.isArray(candidate.ownedEntries) || Array.isArray(candidate.companyEntries);
  }

  protected isHouseRulesSectionPayload(
    value: HospitalityDetails['houseRules']
  ): value is HospitalityHouseRuleSection {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as HospitalityHouseRuleSection;
    return Array.isArray(candidate.ownedEntries)
      || Array.isArray(candidate.companyEntries)
      || Array.isArray(candidate.companyEntryIds);
  }

  protected isAdditionalInfoSectionPayload(
    value: HospitalityDetails['additionalInfo']
  ): value is HospitalityAdditionalInfoSection {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as HospitalityAdditionalInfoSection;
    return Array.isArray(candidate.ownedEntries)
      || Array.isArray(candidate.companyEntries)
      || Array.isArray(candidate.companyEntryIds);
  }

  protected normalizeLegacyHouseRuleOwnedEntries(
    legacy: LegacyHospitalityHouseRules | undefined,
  ): HospitalityHouseRuleOwnedEntry[] {
    if (!legacy || typeof legacy !== 'object') {
      return [];
    }

    const entries: HospitalityHouseRuleOwnedEntry[] = [];
    let order = 0;

    const intro = this.cleanString(legacy.summary) || this.cleanString((legacy as any).introduction);
    if (intro) {
      const entry: HospitalityHouseRuleOwnedEntry = {
        rule: intro,
        order: order++,
        isDeleted: false,
      };
      const links = Array.isArray(legacy.links) ? legacy.links : [];
      if (links.length) {
        entry.links = links;
      }
      entries.push(entry);
    }

    const rules = Array.isArray(legacy.rules) ? legacy.rules : [];
    rules.forEach(rule => {
      const text = this.cleanString(rule);
      if (!text) {
        return;
      }
      entries.push({
        rule: text,
        order: order++,
        isDeleted: false,
      });
    });

    return entries;
  }

  protected normalizeHouseRuleOwnedEntry(
    entry: Partial<HospitalityHouseRuleOwnedEntry> | undefined,
  ): HospitalityHouseRuleOwnedEntry {
    const rule =
      this.cleanString(entry?.rule)
      || this.cleanString((entry as any)?.title)
      || this.cleanString((entry as any)?.summary)
      || this.cleanString((entry as any)?.details)
      || '';

    const normalized: HospitalityHouseRuleOwnedEntry = {
      ...(entry || {}),
      rule,
    } as HospitalityHouseRuleOwnedEntry;

    if (entry?.links) {
      normalized.links = entry.links;
    }

    return normalized;
  }

  protected normalizeApplianceGuideSteps(
    stepsInput?: Array<string | null | undefined> | null,
    detailsInput?: string | null | undefined,
  ): string[] {
    const fromSteps = Array.isArray(stepsInput)
      ? stepsInput
        .map(step => this.cleanString(step) || '')
        .map(step => step.trim())
        .filter(step => step.length)
      : [];
    if (fromSteps.length) {
      return fromSteps;
    }
    const details = this.cleanString(detailsInput);
    if (!details) {
      return [];
    }
    return details
      .split(/\r?\n/)
      .map(step => step.trim())
      .filter(step => step.length);
  }

  protected normalizeApplianceGuideOwnedEntry(
    entry?: (HospitalityApplianceGuideOwnedEntry & { details?: string | null }) | null,
  ): HospitalityApplianceGuideOwnedEntry {
    const normalizedSteps = this.normalizeApplianceGuideSteps(
      Array.isArray(entry?.steps) ? entry?.steps : undefined,
      (entry as any)?.details,
    );

    const normalizedResources = this.mergeHospitalityLinks(
      entry?.resources as Array<HospitalityLink | null | undefined> | undefined,
      (entry as any)?.links as Array<HospitalityLink | null | undefined> | undefined,
    );

    const normalized: HospitalityApplianceGuideOwnedEntry = {
      ...(entry || ({} as HospitalityApplianceGuideOwnedEntry)),
      steps: normalizedSteps,
    };

    if (normalizedResources.length) {
      normalized.resources = normalizedResources;
    } else {
      delete (normalized as any).resources;
    }

    delete (normalized as any).details;

    if (!Array.isArray(normalized.steps)) {
      normalized.steps = [];
    }

    return normalized;
  }

  protected normalizeAdditionalInfoOwnedEntry(
    entry?: (HospitalityAdditionalInfoOwnedEntry & { details?: string | null; body?: string | null }) | null,
  ): HospitalityAdditionalInfoOwnedEntry {
    const normalizedLinks = Array.isArray((entry as any)?.links)
      ? (entry as any).links
          .map((link: HospitalityLink | undefined) => this.cleanLink(link))
          .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link)
      : [];

    const normalized: HospitalityAdditionalInfoOwnedEntry = {
      ...(entry || ({} as HospitalityAdditionalInfoOwnedEntry)),
      title: typeof (entry as any)?.title === 'string' ? (entry as any).title : '',
      body:
        typeof (entry as any)?.body === 'string'
          ? (entry as any).body
          : typeof (entry as any)?.details === 'string'
            ? (entry as any).details
            : '',
    };

    if (normalizedLinks.length) {
      normalized.links = normalizedLinks;
    } else {
      delete (normalized as any).links;
    }

    delete (normalized as any).details;

    return normalized;
  }

  protected normalizeLegacyApplianceGuideOwnedEntries(
    legacy: HospitalityApplianceGuide[] | undefined,
  ): HospitalityApplianceGuideOwnedEntry[] {
    if (!Array.isArray(legacy)) {
      return [];
    }

    return legacy.map((guide, index) => {
      const appliance = this.cleanString(guide?.appliance) || '';
      const summary = this.cleanString(guide?.summary) || '';
      const steps = this.normalizeApplianceGuideSteps(
        Array.isArray(guide?.steps) ? guide?.steps || [] : undefined,
        guide?.details,
      );
      const resources = this.mergeHospitalityLinks(
        guide?.resources as Array<HospitalityLink | null | undefined> | undefined,
        (guide as any)?.links as Array<HospitalityLink | null | undefined> | undefined,
      );

      const entry: HospitalityApplianceGuideOwnedEntry = {
        appliance,
        summary,
        steps,
      };
      if (!entry.steps.length && (summary || appliance)) {
        entry.steps = summary ? [summary] : appliance ? [appliance] : [];
      }
      if (resources.length) {
        entry.resources = resources;
      }
      entry.order = index;
      return entry;
    });
  }

  protected normalizeApplianceGuideEntries(details?: HospitalityDetails): {
    ownedEntries: HospitalityApplianceGuideOwnedEntry[];
    selectedCompanyIds: string[];
  } {
    const applianceGuides = details?.applianceGuides;
    if (this.isApplianceGuidesSectionPayload(applianceGuides)) {
      const ownedEntries = Array.isArray(applianceGuides.ownedEntries)
        ? applianceGuides.ownedEntries.map(entry => this.normalizeApplianceGuideOwnedEntry(entry))
        : [];
      const selectionOrder = new Map<string, number>();
      const addSelection = (value?: string | null, orderHint?: number | null): void => {
        const normalized = this.cleanString(value);
        if (!normalized || selectionOrder.has(normalized)) {
          return;
        }
        if (typeof orderHint === 'number' && Number.isFinite(orderHint)) {
          selectionOrder.set(normalized, orderHint);
          return;
        }
        selectionOrder.set(normalized, selectionOrder.size);
      };

      const companyEntries = Array.isArray(applianceGuides.companyEntries) ? applianceGuides.companyEntries : [];
      companyEntries.forEach(entry => {
        if (entry?.isDeleted) {
          return;
        }
        addSelection(entry?.entryId, entry?.order ?? null);
      });

      const companyEntryIds = this.buildCompanyEntries<HospitalityApplianceGuideCompanyEntry>(
        applianceGuides.companyEntryIds
      );
      companyEntryIds.forEach(entry => addSelection(entry?.entryId, entry?.order ?? null));

      const explicitSelections = Array.isArray(applianceGuides.selectedCompanyEntryIds)
        ? applianceGuides.selectedCompanyEntryIds
        : [];
      explicitSelections
        .map(id => this.cleanString(id))
        .filter((id): id is string => !!id)
        .forEach((id, index) => addSelection(id, index));

      const selectedCompanyIds = Array.from(selectionOrder.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id);

      return { ownedEntries, selectedCompanyIds };
    }

    const legacyOwned = this.normalizeLegacyApplianceGuideOwnedEntries(
      Array.isArray(applianceGuides) ? applianceGuides : []
    );
    const legacyCompanyIds = Array.isArray(details?.selectedCompanyApplianceGuideIds)
      ? details!.selectedCompanyApplianceGuideIds.filter((id): id is string => !!this.cleanString(id))
      : [];

    return { ownedEntries: legacyOwned, selectedCompanyIds: legacyCompanyIds };
  }

  protected normalizeHouseRuleEntries(details?: HospitalityDetails): {
    ownedEntries: HospitalityHouseRuleOwnedEntry[];
    selectedCompanyIds: string[];
  } {
    const houseRules = details?.houseRules;
    if (this.isHouseRulesSectionPayload(houseRules)) {
      const ownedEntries = Array.isArray(houseRules.ownedEntries)
        ? houseRules.ownedEntries.map(entry => this.normalizeHouseRuleOwnedEntry(entry))
        : [];
      const selectionOrder = new Map<string, number>();
      const addSelection = (value?: string | null, orderHint?: number | null): void => {
        const normalized = this.cleanString(value);
        if (!normalized || selectionOrder.has(normalized)) {
          return;
        }
        if (typeof orderHint === 'number' && Number.isFinite(orderHint)) {
          selectionOrder.set(normalized, orderHint);
          return;
        }
        selectionOrder.set(normalized, selectionOrder.size);
      };

      const companyEntries = Array.isArray(houseRules.companyEntries) ? houseRules.companyEntries : [];
      companyEntries.forEach(entry => {
        if (entry?.isDeleted) {
          return;
        }
        addSelection(entry?.entryId, entry?.order ?? null);
      });

      const companyEntryIds = this.buildCompanyEntries<HospitalityCompanyEntry>(houseRules.companyEntryIds);
      companyEntryIds.forEach(entry => addSelection(entry?.entryId, entry?.order ?? null));

      const explicitSelections = Array.isArray(houseRules.selectedCompanyEntryIds)
        ? houseRules.selectedCompanyEntryIds
        : [];
      explicitSelections.forEach((rawId: string | null | undefined, index: number) => {
        const normalizedId = this.cleanString(rawId);
        if (!normalizedId) {
          return;
        }
        addSelection(normalizedId, index);
      });

      const selectedCompanyIds = Array.from(selectionOrder.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id);

      return { ownedEntries, selectedCompanyIds };
    }

    const ownedEntries = this.normalizeLegacyHouseRuleOwnedEntries(houseRules as LegacyHospitalityHouseRules | undefined);
    const legacyCompanyIds = Array.isArray(details?.selectedCompanyHouseRuleIds)
      ? details!.selectedCompanyHouseRuleIds.filter((id): id is string => !!this.cleanString(id))
      : [];

    return { ownedEntries, selectedCompanyIds: legacyCompanyIds };
  }

  protected normalizeAdditionalInfoEntries(details?: HospitalityDetails): {
    ownedEntries: HospitalityAdditionalInfoOwnedEntry[];
    selectedCompanyIds: string[];
  } {
    const additionalInfo = details?.additionalInfo;
    if (this.isAdditionalInfoSectionPayload(additionalInfo)) {
      const ownedEntries = Array.isArray(additionalInfo.ownedEntries)
        ? additionalInfo.ownedEntries.map(entry => this.normalizeAdditionalInfoOwnedEntry(entry))
        : [];
      const selectionOrder = new Map<string, number>();
      const addSelection = (value?: string | null, orderHint?: number | null): void => {
        const normalized = this.cleanString(value);
        if (!normalized || selectionOrder.has(normalized)) {
          return;
        }
        if (typeof orderHint === 'number' && Number.isFinite(orderHint)) {
          selectionOrder.set(normalized, orderHint);
          return;
        }
        selectionOrder.set(normalized, selectionOrder.size);
      };

      const companyEntries = Array.isArray(additionalInfo.companyEntries) ? additionalInfo.companyEntries : [];
      companyEntries.forEach(entry => {
        if (entry?.isDeleted) {
          return;
        }
        addSelection(entry?.entryId, entry?.order ?? null);
      });

      const companyEntryIds = this.buildCompanyEntries<HospitalityCompanyEntry>(additionalInfo.companyEntryIds);
      companyEntryIds.forEach(entry => addSelection(entry?.entryId, entry?.order ?? null));

      const explicitSelections = Array.isArray(additionalInfo.selectedCompanyEntryIds)
        ? additionalInfo.selectedCompanyEntryIds
        : [];
      explicitSelections.forEach((rawId: string | null | undefined, index: number) => {
        const normalizedId = this.cleanString(rawId);
        if (!normalizedId) {
          return;
        }
        addSelection(normalizedId, index);
      });

      const selectedCompanyIds = Array.from(selectionOrder.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id);

      return { ownedEntries, selectedCompanyIds };
    }

    const legacySections = Array.isArray(details?.infoSections) ? details!.infoSections : [];
    const ownedEntries = legacySections
      .map((section): HospitalityAdditionalInfoOwnedEntry | null => {
        if (!section) {
          return null;
        }
        const title = this.cleanString((section as any)?.title);
        const body = this.cleanString((section as any)?.body);
        if (!title && !body) {
          return null;
        }
        const links = Array.isArray((section as any)?.links)
          ? (section as any).links
              .map((link: HospitalityLink | null | undefined) => this.cleanLink(link || undefined))
              .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link)
          : undefined;
        const entry = this.normalizeAdditionalInfoOwnedEntry({
          title: title || body || '',
          body: body || title || '',
          links,
        } as HospitalityAdditionalInfoOwnedEntry);
        return entry;
      })
      .filter((entry): entry is HospitalityAdditionalInfoOwnedEntry => !!entry);

    return { ownedEntries, selectedCompanyIds: [] };
  }

  protected createLinkGroup(link?: HospitalityLink): FormGroup {
    return this.fb.group({
      label: [
        link?.label || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.linkLabel)],
      ],
      url: [
        link?.url || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.linkUrl)],
      ],
      address: [
        normalizeMapAddressInput(link?.address) || link?.address || '',
        [
          Validators.maxLength(this.HOSPITALITY_LIMITS.location),
          mapAddressValidator(),
        ],
      ],
      coordinates: this.fb.group({
        lat: [
          this.formatCoordinateInput(link?.coordinates?.lat),
          [mapLatitudeValidator()],
        ],
        lng: [
          this.formatCoordinateInput(link?.coordinates?.lng),
          [mapLongitudeValidator()],
        ],
      }),
      document: [link?.document || null],
    });
  }

  protected createDigitalKeyGroup(key?: HospitalityDigitalKey | HospitalityLink | null): FormGroup {
    const normalized = this.normalizeDigitalKeyInput(key);
    const group = this.fb.group({
      accessType: [normalized?.accessType || null],
      code: [normalized?.code || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.digitalKeyCode)]],
      link: [normalized?.link || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.digitalKeyLink)]],
      notes: [normalized?.notes || '', [Validators.maxLength(this.HOSPITALITY_LIMITS.digitalKeyNotes)]],
    });
    group.setValidators(this.digitalKeyValueValidator());
    return group;
  }

  protected digitalKeyValueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }
      const accessType = this.normalizeDigitalKeyAccessType(control.get('accessType')?.value);
      const code = this.cleanString(control.get('code')?.value);
      const link = this.cleanString(control.get('link')?.value);
      const notes = this.cleanString(control.get('notes')?.value);

      const hasAnyValue = !!(accessType || code || link || notes);

      if (!hasAnyValue) {
        return null;
      }

      if (!accessType) {
        return { digitalKeyAccessRequired: true };
      }

      if (accessType === 'pin') {
        return code ? null : { digitalKeyValueRequired: 'code' };
      }

      if (accessType === 'app') {
        return link ? null : { digitalKeyValueRequired: 'link' };
      }

      if (accessType === 'other') {
        return notes ? null : { digitalKeyValueRequired: 'notes' };
      }

      return { digitalKeyAccessRequired: true };
    };
  }

  protected createMapHighlightGroup(link?: HospitalityLink): FormGroup {
    return this.fb.group({
      label: [
        link?.label || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.linkLabel)],
      ],
      address: [
        normalizeMapAddressInput(link?.address) || link?.address || '',
        [
          Validators.maxLength(this.HOSPITALITY_LIMITS.location),
          mapAddressValidator(),
        ],
      ],
      url: [
        link?.url || '',
        [Validators.maxLength(this.HOSPITALITY_LIMITS.linkUrl)],
      ],
      coordinates: this.fb.group({
        lat: [
          this.formatCoordinateInput(link?.coordinates?.lat),
          [mapLatitudeValidator()],
        ],
        lng: [
          this.formatCoordinateInput(link?.coordinates?.lng),
          [mapLongitudeValidator()],
        ],
      }),
    });
  }

  protected createInfoSectionGroup(section?: HospitalityAdditionalInfoOwnedEntry): FormGroup {
    return this.createAdditionalInfoOwnedEntryGroup(section);
  }

  protected normalizeOptionString(value?: string | null): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }

  protected createCompanySelectionId(prefix: string, value: string, index: number): string {
    const normalizedPrefix = this.normalizeOptionString(prefix)?.toLowerCase() || 'item';
    const normalizedValue = this.normalizeOptionString(value)?.toLowerCase() || '';
    const safeIndex = Number.isFinite(index) ? index : 0;
    return `${normalizedPrefix}:${safeIndex}:${normalizedValue}`;
  }

  protected sanitizeDomId(value: string): string {
    return value.replace(/[^a-zA-Z0-9_-]+/g, '-');
  }

  protected resolveHospitalityGroup(form: FormGroup): FormGroup {
    const hospitalityDetails = form.get('hospitalityDetails');
    if (hospitalityDetails instanceof FormGroup) {
      return hospitalityDetails;
    }
    return form;
  }

  getHospitalityRadioName(
    form: FormGroup,
    section: 'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo'
  ): string {
    let id = this.hospitalityFormIds.get(form);
    if (!id) {
      this.hospitalityFormCounter += 1;
      id = `hospitality-${this.hospitalityFormCounter}`;
      this.hospitalityFormIds.set(form, id);
    }
    return `${id}-${section}`;
  }

  getCompanyListPanelId(
    form: FormGroup,
    section: 'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo'
  ): string {
    return `${this.getHospitalityRadioName(form, section)}-company`;
  }

  getPropertyPanelId(
    form: FormGroup,
    section: 'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo',
    index?: number
  ): string {
    const suffix = index !== undefined ? `-${index}` : '';
    return `${this.getHospitalityRadioName(form, section)}-property${suffix}`;
  }

  protected ensureCompanyPreviewState(
    form: FormGroup
  ): Record<'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo', Set<string>> {
    const hospitalityGroup = this.resolveHospitalityGroup(form);
    let state = this.companyPreviewStateCache.get(hospitalityGroup);
    if (!state) {
      state = {
        houseRules: new Set<string>(),
        applianceGuides: new Set<string>(),
        checkOut: new Set<string>(),
        checkIn: new Set<string>(),
        additionalInfo: new Set<string>(),
      };
      this.companyPreviewStateCache.set(hospitalityGroup, state);
    }
    return state;
  }

  getCompanyPreviewPanelId(
    form: FormGroup,
    section: 'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo',
    id: string
  ): string {
    return `${this.getHospitalityRadioName(form, section)}-company-preview-${this.sanitizeDomId(id)}`;
  }

  isCompanyPreviewOpen(
    form: FormGroup,
    section: 'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo',
    id: string
  ): boolean {
    return this.ensureCompanyPreviewState(form)[section].has(id);
  }

  toggleCompanyPreview(
    form: FormGroup,
    section: 'houseRules' | 'applianceGuides' | 'checkOut' | 'checkIn' | 'additionalInfo',
    id: string
  ): void {
    const set = this.ensureCompanyPreviewState(form)[section];
    if (set.has(id)) {
      set.delete(id);
    } else {
      set.add(id);
    }
  }

  protected ensureOwnedEntryPreviewState(group: FormGroup): {
    houseRules: boolean;
    checkOut: boolean;
    additionalInfo: boolean;
    applianceGuides: Set<number>;
    applianceGuidesOpen: boolean;
  } {
    let state = this.propertyPreviewStates.get(group);
    if (!state) {
      state = {
        houseRules: false,
        checkOut: false,
        additionalInfo: false,
        applianceGuides: new Set<number>(),
        applianceGuidesOpen: false,
      };
      this.propertyPreviewStates.set(group, state);
    }
    return state;
  }

  isOwnedEntryPreviewOpen(
    group: FormGroup,
    section: 'houseRules' | 'checkOut' | 'applianceGuides' | 'additionalInfo',
    index?: number
  ): boolean {
    const state = this.ensureOwnedEntryPreviewState(group);
    if (section === 'applianceGuides') {
      if (index !== undefined) {
        return state.applianceGuides.has(index);
      }
      return state.applianceGuidesOpen;
    }
    return state[section];
  }

  protected setOwnedEntryPreviewOpen(
    group: FormGroup,
    section: 'houseRules' | 'checkOut' | 'applianceGuides' | 'additionalInfo',
    open: boolean,
    index?: number
  ): void {
    const state = this.ensureOwnedEntryPreviewState(group);
    if (section === 'applianceGuides') {
      if (index === undefined) {
        if (open) {
          this.setAllApplianceGuidePreviewState(group, true);
        } else {
          state.applianceGuides.clear();
          state.applianceGuidesOpen = false;
        }
        return;
      }
      if (open) {
        state.applianceGuides.add(index);
        state.applianceGuidesOpen = true;
      } else {
        state.applianceGuides.delete(index);
        if (!state.applianceGuides.size) {
          state.applianceGuidesOpen = false;
        }
      }
      return;
    }
    state[section] = open;
  }

  toggleOwnedEntryPreview(
    group: FormGroup,
    section: 'houseRules' | 'checkOut' | 'additionalInfo'
  ): void {
    const open = this.isOwnedEntryPreviewOpen(group, section);
    this.setOwnedEntryPreviewOpen(group, section, !open);
  }

  toggleApplianceGuidePreview(group: FormGroup, index?: number): void {
    if (index === undefined) {
      const open = this.isOwnedEntryPreviewOpen(group, 'applianceGuides');
      this.setOwnedEntryPreviewOpen(group, 'applianceGuides', !open);
      return;
    }
    const open = this.isOwnedEntryPreviewOpen(group, 'applianceGuides', index);
    this.setOwnedEntryPreviewOpen(group, 'applianceGuides', !open, index);
  }

  protected shiftApplianceGuidePreviewState(group: FormGroup, removedIndex: number): void {
    const state = this.ensureOwnedEntryPreviewState(group);
    if (!state.applianceGuides.size) {
      return;
    }
    const updated = new Set<number>();
    state.applianceGuides.forEach(index => {
      if (index === removedIndex) {
        return;
      }
      updated.add(index > removedIndex ? index - 1 : index);
    });
    state.applianceGuides = updated;
    if (!state.applianceGuides.size) {
      state.applianceGuidesOpen = false;
    }
  }

  protected setAllApplianceGuidePreviewState(group: FormGroup, open: boolean): void {
    const state = this.ensureOwnedEntryPreviewState(group);
    if (!open) {
      state.applianceGuides.clear();
      state.applianceGuidesOpen = false;
      return;
    }
    const guidesGroup = group.get('applianceGuides') as FormGroup | null;
    const ownedEntries = guidesGroup?.get('ownedEntries') as FormArray | null;
    state.applianceGuides.clear();
    ownedEntries?.controls.forEach((_, index) => state.applianceGuides.add(index));
    state.applianceGuidesOpen = true;
  }

  protected buildCompanyHospitalityOptions(): void {
    this.companyHouseRuleOptions = [];
    this.companyApplianceGuideOptions = [];
    this.companyCheckOutOptions = [];
    this.companyCheckInOptions = [];
    this.companyAdditionalInfoOptions = [];

    const normalizeLinks = (links?: Array<HospitalityLink | null | undefined>): HospitalityLink[] | undefined => {
      if (!Array.isArray(links)) {
        return undefined;
      }
      const cleaned = links
        .map(link => this.cleanLink(link || undefined))
        .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link);
      return cleaned.length ? cleaned : undefined;
    };

    const normalizeDigitalKeys = (
      keys?: Array<HospitalityDigitalKey | HospitalityLink | null | undefined>,
    ): HospitalityDigitalKey[] | undefined => {
      if (!Array.isArray(keys)) {
        return undefined;
      }
      const cleaned = keys
        .map(key => this.cleanDigitalKey(key || undefined))
        .filter((key: HospitalityDigitalKey | undefined): key is HospitalityDigitalKey => !!key);
      return cleaned.length ? cleaned : undefined;
    };

    const landing = this.company?.hospitalityLanding;
    const landingSections = landing?.infoSections || [];

    const buildAdditionalInfoOptionsFromLanding = (): CompanyAdditionalInfoOption[] => {
      if (!landingSections.length) {
        return [];
      }
      const reservedKeys = new Set(['houserules', 'applianceguides', 'checkout', 'checkin']);
      return landingSections
        .map((section: any, index: number): CompanyAdditionalInfoOption | null => {
          if (!section) {
            return null;
          }
          const key = this.normalizeOptionString((section as any)?.key);
          if (key && reservedKeys.has(key.toLowerCase())) {
            return null;
          }
          const title = this.normalizeOptionString((section as any)?.title);
          const summary = this.normalizeOptionString((section as any)?.summary);
          const body = this.normalizeOptionString((section as any)?.body ?? (section as any)?.details);
          const links = normalizeLinks((section as any)?.links);
          if (!(title || summary || body || (links && links.length))) {
            return null;
          }
          const idSource = title || summary || body || key || 'section';
          return {
            id: this.createCompanySelectionId('additionalInfo', idSource, index),
            title,
            summary,
            body,
            links,
          };
        })
        .filter((option: CompanyAdditionalInfoOption | null): option is CompanyAdditionalInfoOption => !!option);
    };

    const buildAdditionalInfoOptionsFromCatalog = (): CompanyAdditionalInfoOption[] => {
      const catalog = Array.isArray(this.company?.hospitalityAdditionalInfos)
        ? this.company?.hospitalityAdditionalInfos ?? []
        : [];
      return catalog
        .map((entry: any, index: number): CompanyAdditionalInfoOption | null => {
          const title = this.normalizeOptionString((entry as any)?.title);
          const summary = this.normalizeOptionString((entry as any)?.summary);
          const body = this.normalizeOptionString((entry as any)?.body ?? (entry as any)?.details);
          const links = normalizeLinks((entry as any)?.links);
          if (!(title || summary || body || (links && links.length))) {
            return null;
          }
          const rawId = this.normalizeOptionString((entry as any)?.id)
            || this.normalizeOptionString((entry as any)?._id);
          const id = rawId || this.createCompanySelectionId('additionalInfo', title || summary || 'entry', index);
          return {
            id,
            title,
            summary,
            body,
            links,
          };
        })
        .filter((option: CompanyAdditionalInfoOption | null): option is CompanyAdditionalInfoOption => !!option);
    };

    const applyAdditionalInfoFallback = (): void => {
      if (this.companyAdditionalInfoOptions.length) {
        return;
      }
      const landingOptions = buildAdditionalInfoOptionsFromLanding();
      if (landingOptions.length) {
        this.companyAdditionalInfoOptions = landingOptions;
        return;
      }
      const catalogOptions = buildAdditionalInfoOptionsFromCatalog();
      if (catalogOptions.length) {
        this.companyAdditionalInfoOptions = catalogOptions;
      }
    };

    const selections = this.company?.hospitalitySelections;
    if (selections) {
      const trimId = (value?: string | null): string | undefined => {
        if (typeof value !== 'string') {
          return undefined;
        }
        const trimmed = value.trim();
        return trimmed.length ? trimmed : undefined;
      };

      const houseRuleOptions = Array.isArray(selections.houseRules?.items)
        ? selections.houseRules.items
          .map((item: any): CompanyHouseRuleOption | null => {
            const id = trimId(item?.id || (item as any)?.entryId);
            const label = this.normalizeOptionString(item?.label);
            if (!(id && label)) {
              return null;
            }
            const links = normalizeLinks((item as any)?.links);
            return links && links.length ? { id, label, links } : { id, label };
          })
          .filter((option: CompanyHouseRuleOption | null): option is CompanyHouseRuleOption => !!option)
        : [];

      const applianceGuideOptions = Array.isArray(selections.applianceGuides)
        ? (selections.applianceGuides as Array<{
          id?: string | null;
          appliance?: string | null;
          summary?: string | null;
          steps?: Array<string | null | undefined> | null;
          resources?: Array<HospitalityLink | null | undefined> | null;
        }>)
          .map((guide): CompanyApplianceGuideOption | null => {
            const id = trimId(guide?.id || (guide as any)?.entryId);
            if (!id) {
              return null;
            }

            const appliance = this.normalizeOptionString(guide?.appliance);
            const summary = this.normalizeOptionString(guide?.summary);

            const steps = Array.isArray(guide?.steps)
              ? guide.steps
                .map((step: string | null | undefined) => this.normalizeOptionString(step))
                .filter((step): step is string => !!step)
              : undefined;

            const resources = this.mergeHospitalityLinks(
              guide?.resources as Array<HospitalityLink | null | undefined> | undefined,
              (guide as any)?.links as Array<HospitalityLink | null | undefined> | undefined,
            );

            return {
              id,
              appliance,
              summary,
              steps,
              resources: resources.length ? resources : undefined,
            };
          })
          .filter((option: CompanyApplianceGuideOption | null): option is CompanyApplianceGuideOption => !!option)
        : [];

      const checkOutItems = Array.isArray(selections.checkOut?.items)
        ? (selections.checkOut.items as Array<any>)
        : Array.isArray(selections.checkOut)
          ? (selections.checkOut as Array<any>)
          : [];

      const checkOutOptions = checkOutItems
        .map((item: any): CompanyCheckOutOption | null => {
          const id = trimId(item?.id || item?.entryId);
          if (!id) {
            return null;
          }

          const title = this.normalizeOptionString(
            item?.title
            ?? item?.label
            ?? item?.summary
            ?? item?.details
          );
          const instructions = this.normalizeOptionString(
            item?.instructions
            ?? item?.summary
            ?? item?.details
          );
          const checkOutTime = this.normalizeOptionString(item?.checkOutTime ?? item?.deadline ?? item?.until);
          const location = this.normalizeOptionString(item?.dropOffLocation ?? item?.location);
          const contact = this.normalizeOptionString(item?.contact);

          const label = title
            || instructions
            || location
            || contact
            || checkOutTime
            || this.normalizeOptionString(item?.label)
            || id;

          const links = normalizeLinks(item?.links);

          const checklistSource = item?.checklist as
            | { title?: string | null; items?: Array<string | null | undefined> | null }
            | undefined;
          const checklistTitle = this.normalizeOptionString(checklistSource?.title);
          const rawChecklistItems: Array<string | null | undefined> = Array.isArray(checklistSource?.items)
            ? (checklistSource?.items as Array<string | null | undefined>)
            : [];
          const checklistItems = rawChecklistItems
            .map((entry) => this.normalizeOptionString(entry))
            .filter((entry): entry is string => !!entry);

          const checklist = checklistTitle || checklistItems.length
            ? {
                ...(checklistTitle ? { title: checklistTitle } : {}),
                ...(checklistItems.length ? { items: checklistItems } : {}),
              }
            : undefined;

          return {
            id,
            label,
            title,
            instructions,
            checkOutTime,
            dropOffLocation: location,
            contact,
            links,
            checklist,
          };
        })
        .filter((option: CompanyCheckOutOption | null): option is CompanyCheckOutOption => !!option);

      const checkInItems = selections.checkIn as
        | Array<{
            id?: string | null;
            label?: string | null;
            summary?: string | null;
            details?: string | null;
            instructions?: string | null;
            location?: string | null;
            contact?: string | null;
            from?: string | null;
            until?: string | null;
            schedule?: { from?: string | null; until?: string | null } | null;
            links?: Array<HospitalityLink | null | undefined> | null;
            digitalKeys?: Array<HospitalityDigitalKey | HospitalityLink | null | undefined> | null;
          }>
        | { items?: Array<any> };

      const rawCheckInItems = Array.isArray((checkInItems as any)?.items)
        ? (checkInItems as any).items
        : Array.isArray(checkInItems)
          ? (checkInItems as Array<any>)
          : [];

      const checkInOptions = rawCheckInItems
        .map((item: any, index: number): CompanyCheckInOption | null => {
          const id = trimId(item?.id || item?.entryId);
          if (!id) {
            return null;
          }

          const scheduleSource = item?.schedule && typeof item.schedule === 'object'
            ? item.schedule
            : undefined;

          const from = this.normalizeOptionString(scheduleSource?.from ?? item?.from);
          const until = this.normalizeOptionString(scheduleSource?.until ?? item?.until);

          const titleSource = this.normalizeOptionString(item?.title ?? item?.label ?? item?.summary ?? item?.instructions);
          const instructions = this.normalizeOptionString(item?.instructions ?? item?.summary ?? item?.details);
          const keyPickupLocation = this.normalizeOptionString((item as any)?.keyPickupLocation ?? item?.location);
          const contact = this.normalizeOptionString(item?.contact);

          const links = normalizeLinks(item?.links);
          const digitalKeys = normalizeDigitalKeys(item?.digitalKeys);

          const schedule = from || until ? { from, until } : undefined;

          const title = titleSource
            || instructions
            || keyPickupLocation
            || contact
            || (from || until ? `Check-in ${from || '—'} – ${until || '—'}` : undefined)
            || id;

          return {
            id,
            title,
            instructions,
            keyPickupLocation,
            contact,
            schedule,
            links,
            digitalKeys,
          };
        })
        .filter((option: CompanyCheckInOption | null): option is CompanyCheckInOption => !!option);

      const additionalInfoItems = Array.isArray(selections.additionalInfo)
        ? selections.additionalInfo
        : Array.isArray((selections.additionalInfo as any)?.items)
          ? (selections.additionalInfo as any).items
          : [];

      const additionalInfoOptions = additionalInfoItems
        .map((item: any, index: number): CompanyAdditionalInfoOption | null => {
          const id = trimId(item?.id || item?.entryId);
          if (!id) {
            return null;
          }
          const title = this.normalizeOptionString(item?.title ?? item?.label);
          const summary = this.normalizeOptionString(item?.summary);
          const body = this.normalizeOptionString(item?.body ?? item?.details ?? item?.description);
          const links = normalizeLinks(item?.links);
          return {
            id,
            title,
            summary,
            body,
            links,
          };
        })
        .filter((option: CompanyAdditionalInfoOption | null): option is CompanyAdditionalInfoOption => !!option);

      this.companyHouseRuleOptions = houseRuleOptions;
      this.companyApplianceGuideOptions = applianceGuideOptions;
      this.companyCheckOutOptions = checkOutOptions;
      this.companyCheckInOptions = checkInOptions;
      this.companyAdditionalInfoOptions = additionalInfoOptions;

      if (
        houseRuleOptions.length
        || applianceGuideOptions.length
        || checkOutOptions.length
        || checkInOptions.length
        || additionalInfoOptions.length
      ) {
        applyAdditionalInfoFallback();
        return;
      }
    }

    if (landingSections.length) {
      const findSection = (key: string) => landingSections.find((section: any) => (section?.key || '').trim().toLowerCase() === key);

      const houseRulesSection = findSection('houserules');
      if (houseRulesSection) {
        const body = houseRulesSection.body || '';
        const rules = body
          .split(/\r?\n/)
          .map((rule: string) => rule.trim())
          .filter((rule: string) => rule.length);
        this.companyHouseRuleOptions = rules.map((rule: string, index: number) => ({
          id: this.createCompanySelectionId('houseRule', rule, index),
          label: rule,
        }));
      }

      const applianceGuideSection = findSection('applianceguides');
      if (applianceGuideSection) {
        const title = this.normalizeOptionString(applianceGuideSection.title);
        const bodyText = this.normalizeOptionString(applianceGuideSection.body);
        const steps = this.normalizeApplianceGuideSteps(
          Array.isArray((applianceGuideSection as any).steps)
            ? (applianceGuideSection as any).steps
            : null,
          applianceGuideSection.body,
        );
        const resources = this.mergeHospitalityLinks(
          applianceGuideSection.links as Array<HospitalityLink | null | undefined> | undefined,
        );

        let summary = bodyText;
        if (!summary && steps.length === 1) {
          summary = steps[0];
        }
        const identifierValue = `${(title || '').toLowerCase()}|${(summary || '').toLowerCase()}`;
        const id = this.createCompanySelectionId('applianceGuide', identifierValue, 0);
        this.companyApplianceGuideOptions = [
          {
            id,
            appliance: title,
            summary,
            steps: steps.length ? steps : undefined,
            resources: resources.length ? resources : undefined,
          }
        ];
      }

      const checkOutSection = findSection('checkout');
      if (checkOutSection) {
        const items = (checkOutSection.body || '')
          .split(/\r?\n/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length);
        this.companyCheckOutOptions = items.map((item: string, index: number) => ({
          id: this.createCompanySelectionId('checkOut', item, index),
          label: item,
          title: item,
          instructions: item,
        }));
      }

      const checkInSection = findSection('checkin');
      if (checkInSection && !this.companyCheckInOptions.length) {
        const body = this.normalizeOptionString(checkInSection.body);
        if (body) {
          const id = this.createCompanySelectionId('checkIn', body, 0);
          this.companyCheckInOptions = [{
            id,
            title: body,
            instructions: body,
          }];
        }
      }
    }

    applyAdditionalInfoFallback();
    const catalogHouseRules = Array.isArray(this.company?.hospitalityHouseRules)
      ? this.company?.hospitalityHouseRules ?? []
      : [];
    if (!this.companyHouseRuleOptions.length && catalogHouseRules.length) {
      this.companyHouseRuleOptions = catalogHouseRules
        .map((entry: any, index: number): CompanyHouseRuleOption | null => {
          const rawId = this.normalizeOptionString((entry as any)?.id) || this.normalizeOptionString((entry as any)?._id);
          const label = this.normalizeOptionString(
            (entry as any)?.rule
              ?? (entry as any)?.title
              ?? (entry as any)?.summary
              ?? (entry as any)?.details
          );
          if (!label) {
            return null;
          }
          const links = normalizeLinks((entry as any)?.links || (entry as any)?.resources);
          const id = rawId || this.createCompanySelectionId('houseRule', label, index);
          return links && links.length ? { id, label, links } : { id, label };
        })
        .filter((option: CompanyHouseRuleOption | null): option is CompanyHouseRuleOption => !!option);
    }

    const catalogApplianceGuides = Array.isArray(this.company?.hospitalityApplianceGuides)
      ? this.company?.hospitalityApplianceGuides ?? []
      : [];
    if (!this.companyApplianceGuideOptions.length && catalogApplianceGuides.length) {
      this.companyApplianceGuideOptions = catalogApplianceGuides
        .map((entry: any, index: number): CompanyApplianceGuideOption | null => {
          const rawId = this.normalizeOptionString((entry as any)?.id) || this.normalizeOptionString((entry as any)?._id);
          const appliance = this.normalizeOptionString((entry as any)?.appliance);
          const summary = this.normalizeOptionString((entry as any)?.summary);
          const steps = Array.isArray((entry as any)?.steps)
            ? (entry as any).steps
                .map((step: string | null | undefined) => this.normalizeOptionString(step))
                .filter((step: string | null | undefined): step is string => !!step)
            : undefined;
          const resources = this.mergeHospitalityLinks(
            (entry as any)?.resources as Array<HospitalityLink | null | undefined> | undefined,
            (entry as any)?.links as Array<HospitalityLink | null | undefined> | undefined,
          );
          if (!(appliance || summary || (steps && steps.length) || (resources && resources.length))) {
            return null;
          }
          const idSource = appliance || summary || `appliance-${index + 1}`;
          const id = rawId || this.createCompanySelectionId('applianceGuide', idSource, index);
          return {
            id,
            appliance: appliance || undefined,
            summary: summary || undefined,
            steps: steps && steps.length ? steps : undefined,
            resources: resources.length ? resources : undefined,
          };
        })
        .filter((option: CompanyApplianceGuideOption | null): option is CompanyApplianceGuideOption => !!option);
    }

    const catalogCheckOuts = Array.isArray(this.company?.hospitalityCheckOuts)
      ? this.company?.hospitalityCheckOuts ?? []
      : [];
    if (!this.companyCheckOutOptions.length && catalogCheckOuts.length) {
      this.companyCheckOutOptions = catalogCheckOuts
        .map((entry: any, index: number): CompanyCheckOutOption | null => {
          const rawId = this.normalizeOptionString((entry as any)?.id) || this.normalizeOptionString((entry as any)?._id);
          const title = this.normalizeOptionString((entry as any)?.title ?? (entry as any)?.label);
          const instructions = this.normalizeOptionString(
            (entry as any)?.instructions ?? (entry as any)?.summary ?? (entry as any)?.details
          );
          const checkOutTime = this.normalizeOptionString(
            (entry as any)?.checkOutTime ?? (entry as any)?.deadline ?? (entry as any)?.until
          );
          const dropOffLocation = this.normalizeOptionString((entry as any)?.dropOffLocation ?? (entry as any)?.location);
          const contact = this.normalizeOptionString((entry as any)?.contact);
          const links = Array.isArray((entry as any)?.links)
            ? (entry as any).links
                .map((link: HospitalityLink | null | undefined) => this.cleanLink(link || undefined))
                .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link)
            : undefined;
          const checklistSource = (entry as any)?.checklist as
            | { title?: string | null; items?: Array<string | null | undefined> | null }
            | undefined;
          const checklistTitle = this.normalizeOptionString(checklistSource?.title);
          const rawChecklistItems: Array<string | null | undefined> = Array.isArray(checklistSource?.items)
            ? checklistSource.items as Array<string | null | undefined>
            : [];
          const checklistItems = rawChecklistItems
            .map((item: string | null | undefined) => this.normalizeOptionString(item))
            .filter((item: string | null | undefined): item is string => !!item);
          const checklist = checklistTitle || checklistItems.length
            ? {
                ...(checklistTitle ? { title: checklistTitle } : {}),
                ...(checklistItems.length ? { items: checklistItems } : {}),
              }
            : undefined;
          const label = title || instructions || dropOffLocation || contact || checkOutTime;
          if (!(label || (links && links.length) || checklist)) {
            return null;
          }
          const id = rawId || this.createCompanySelectionId('checkOut', label || 'entry', index);
          return {
            id,
            label: label || id,
            title: title || undefined,
            instructions: instructions || undefined,
            checkOutTime: checkOutTime || undefined,
            dropOffLocation: dropOffLocation || undefined,
            contact: contact || undefined,
            links,
            checklist,
          };
        })
        .filter((option: CompanyCheckOutOption | null): option is CompanyCheckOutOption => !!option);
    }

    const catalogCheckIns = Array.isArray(this.company?.hospitalityCheckIns)
      ? this.company?.hospitalityCheckIns ?? []
      : [];
    if (!this.companyCheckInOptions.length && catalogCheckIns.length) {
      this.companyCheckInOptions = catalogCheckIns
        .map((entry: any, index: number): CompanyCheckInOption | null => {
          const rawId = this.normalizeOptionString((entry as any)?.id) || this.normalizeOptionString((entry as any)?._id);
          const title = this.normalizeOptionString((entry as any)?.title);
          const instructions = this.normalizeOptionString(
            (entry as any)?.instructions ?? (entry as any)?.summary ?? (entry as any)?.details
          );
          const keyPickupLocation = this.normalizeOptionString((entry as any)?.keyPickupLocation ?? (entry as any)?.location);
          const contact = this.normalizeOptionString((entry as any)?.contact);
          const scheduleSource = (entry as any)?.schedule && typeof (entry as any).schedule === 'object'
            ? (entry as any).schedule
            : undefined;
          const scheduleFrom = this.normalizeOptionString(scheduleSource?.from ?? (entry as any)?.from);
          const scheduleUntil = this.normalizeOptionString(scheduleSource?.until ?? (entry as any)?.until);
          const schedule = scheduleFrom || scheduleUntil ? { from: scheduleFrom, until: scheduleUntil } : undefined;
          const links = Array.isArray((entry as any)?.links)
            ? (entry as any).links
                .map((link: HospitalityLink | null | undefined) => this.cleanLink(link || undefined))
                .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link)
            : undefined;
          const digitalKeys = Array.isArray((entry as any)?.digitalKeys)
            ? (entry as any).digitalKeys
                .map((key: HospitalityDigitalKey | HospitalityLink | null | undefined) => this.cleanDigitalKey(key || undefined))
                .filter((key: HospitalityDigitalKey | undefined): key is HospitalityDigitalKey => !!key)
            : undefined;
          if (!(title || instructions || keyPickupLocation || contact || schedule || (links && links.length) || (digitalKeys && digitalKeys.length))) {
            return null;
          }
          const id = rawId || this.createCompanySelectionId('checkIn', title || instructions || 'entry', index);
          return {
            id,
            title: title
              || instructions
              || keyPickupLocation
              || contact
              || (scheduleFrom || scheduleUntil ? `Check-in ${scheduleFrom || '—'} – ${scheduleUntil || '—'}` : undefined)
              || id,
            instructions: instructions || undefined,
            keyPickupLocation: keyPickupLocation || undefined,
            contact: contact || undefined,
            schedule,
            links,
            digitalKeys,
          };
        })
        .filter((option: CompanyCheckInOption | null): option is CompanyCheckInOption => !!option);
    }

    if (!this.companyAdditionalInfoOptions.length) {
      this.companyAdditionalInfoOptions = buildAdditionalInfoOptionsFromCatalog();
    }

    if (
      this.companyHouseRuleOptions.length
      || this.companyApplianceGuideOptions.length
      || this.companyCheckOutOptions.length
      || this.companyCheckInOptions.length
      || this.companyAdditionalInfoOptions.length
    ) {
      return;
    }
  }

  protected ensureOwnedEntryDefaultsForHouseRule(entryGroup: FormGroup): void {
    const links = entryGroup.get('links') as FormArray | null;
    if (links && links.length === 0) {
      links.push(this.createLinkGroup());
    }
  }

  protected ensureOwnedEntryDefaultsForCheckOut(entryGroup: FormGroup): void {
    const links = entryGroup.get('links') as FormArray | null;
    if (links && links.length === 0) {
      links.push(this.createLinkGroup());
    }
    const checklist = entryGroup.get('checklist') as FormGroup | null;
    if (checklist) {
      const items = checklist.get('items') as FormArray | null;
      if (items && items.length === 0) {
        items.push(this.fb.control('', [Validators.maxLength(this.HOSPITALITY_LIMITS.checklistItem)]));
      }
    }
  }

  protected ensureOwnedEntryDefaultsForApplianceGuide(entryGroup: FormGroup): void {
    const steps = entryGroup.get('steps') as FormArray | null;
    if (steps && steps.length === 0) {
      steps.push(this.fb.control('', [Validators.maxLength(this.HOSPITALITY_LIMITS.details)]));
    }
    const resources = entryGroup.get('resources') as FormArray | null;
    if (resources && resources.length === 0) {
      resources.push(this.createLinkGroup());
    }
  }

  protected ensureOwnedEntriesForSection(
    hospitalityGroup: FormGroup,
    section: 'houseRules' | 'checkOut' | 'applianceGuides',
    requireEntry: boolean = true,
  ): void {
    const sectionGroup = hospitalityGroup.get(section) as FormGroup | null;
    if (!sectionGroup) {
      return;
    }
    const ownedEntries = sectionGroup.get('ownedEntries') as FormArray | null;
    if (!ownedEntries) {
      return;
    }

    if (requireEntry && ownedEntries.length === 0) {
      switch (section) {
        case 'houseRules':
          ownedEntries.push(this.createHouseRuleOwnedEntryGroup());
          break;
        case 'checkOut':
          ownedEntries.push(this.createCheckOutOwnedEntryGroup());
          break;
        case 'applianceGuides':
          ownedEntries.push(this.createApplianceGuideOwnedEntryGroup());
          break;
        default:
          break;
      }
    }

    ownedEntries.controls.forEach(control => {
      const entryGroup = control as FormGroup;
      switch (section) {
        case 'houseRules':
          this.ensureOwnedEntryDefaultsForHouseRule(entryGroup);
          break;
        case 'checkOut':
          this.ensureOwnedEntryDefaultsForCheckOut(entryGroup);
          break;
        case 'applianceGuides':
          this.ensureOwnedEntryDefaultsForApplianceGuide(entryGroup);
          break;
      }
    });
  }

  protected ensureCheckInDefaults(group: FormGroup): void {
    const checkIn = group.get('checkIn') as FormGroup | null;
    if (!checkIn) {
      return;
    }

    const ownedEntry = checkIn.get('ownedEntry') as FormGroup | null;
    if (ownedEntry) {
      const links = ownedEntry.get('links') as FormArray | null;
      if (links && links.length === 0) {
        links.push(this.createLinkGroup());
      }
      const digitalKeys = ownedEntry.get('digitalKeys') as FormArray | null;
      if (digitalKeys && digitalKeys.length === 0) {
        digitalKeys.push(this.createDigitalKeyGroup());
      }
    }

    const selectionControl = checkIn.get('selectedEntryId') as FormControl | null;
    if (selectionControl && !selectionControl.value) {
      const fallback = this.companyCheckInOptions.find(option => option?.id)?.id || 'new';
      selectionControl.setValue(fallback);
    }
  }

  protected ensureLocalTipsDefaults(group: FormGroup): void {
    const localTips = group.get('localTips') as FormGroup | null;
    if (!localTips) {
      return;
    }
    const highlights = localTips.get('highlights') as FormArray | null;
    if (highlights && highlights.length === 0) {
      highlights.push(this.createMapHighlightGroup());
    }
  }

  protected ensureHospitalityArrayDefaults(control: FormGroup | FormArray): void {
    if (control instanceof FormArray) {
      control.controls.forEach(child => {
        if (child instanceof FormGroup || child instanceof FormArray) {
          this.ensureHospitalityArrayDefaults(child as FormGroup | FormArray);
        }
      });
      return;
    }

    const hospitalityGroup = this.resolveHospitalityGroup(control);
    if (hospitalityGroup !== control) {
      this.ensureHospitalityArrayDefaults(hospitalityGroup);
      return;
    }

    this.ensureOwnedEntriesForSection(hospitalityGroup, 'houseRules');

    this.ensureCheckInDefaults(hospitalityGroup);

    this.ensureOwnedEntriesForSection(hospitalityGroup, 'checkOut');

    this.ensureOwnedEntriesForSection(hospitalityGroup, 'applianceGuides');

    this.ensureLocalTipsDefaults(hospitalityGroup);
  }

  protected hasCheckInOwnedEntryContent(entry?: HospitalityCheckInOwnedEntry): boolean {
    if (!entry) {
      return false;
    }
    if (this.hasEntryMetadata(entry)) {
      return true;
    }
    if (
      this.cleanString(entry.title)
      || this.cleanString(entry.instructions)
      || this.cleanString(entry.keyPickupLocation)
      || this.cleanString(entry.contact)
    ) {
      return true;
    }
    const schedule = entry.schedule || {};
    if (this.cleanString(schedule.from) || this.cleanString(schedule.until)) {
      return true;
    }
    if (this.hasLinksContent(entry.links) || this.hasDigitalKeyContent(entry.digitalKeys)) {
      return true;
    }
    return false;
  }

  protected hasEntryMetadata(entry?: HospitalityEntryMetadata): boolean {
    if (!entry) {
      return false;
    }
    if (this.cleanString(entry._id)) {
      return true;
    }
    if (Number.isFinite(entry.order)) {
      return true;
    }
    if (entry.isDeleted) {
      return true;
    }
    if (
      this.cleanString(entry.appliedAt) ||
      this.cleanString(entry.createdAt) ||
      this.cleanString(entry.updatedAt) ||
      this.cleanString(entry.deletedAt)
    ) {
      return true;
    }
    return false;
  }

  protected hasChecklistContent(checklist?: HospitalityChecklist): boolean {
    if (!checklist) {
      return false;
    }
    if (this.cleanString(checklist.title)) {
      return true;
    }
    return Array.isArray(checklist.items) && checklist.items.some(item => !!this.cleanString(item));
  }

  protected hasLinksContent(links?: HospitalityLink[]): boolean {
    if (!Array.isArray(links)) {
      return false;
    }
    return links.some(link => this.cleanString(link?.label) || this.cleanString(link?.url) || normalizeMapAddressInput(link?.address));
  }

  protected hasCheckOutOwnedEntryContent(entry?: HospitalityCheckOutOwnedEntry): boolean {
    if (!entry) {
      return false;
    }
    if (this.hasEntryMetadata(entry)) {
      return true;
    }
    if (
      this.cleanString(entry.title)
      || this.cleanString(entry.instructions)
      || this.cleanString(entry.checkOutTime)
      || this.cleanString(entry.dropOffLocation)
    ) {
      return true;
    }
    if (this.cleanString(entry.contact)) {
      return true;
    }
    if (this.hasLinksContent(entry.links)) {
      return true;
    }
    return this.hasChecklistContent(entry.checklist);
  }

  protected determineCheckInSelection(
    details: HospitalityDetails | undefined,
    normalized?: { ownedEntries: HospitalityCheckInOwnedEntry[]; companyEntries: HospitalityCompanyEntry[] }
  ): string {
    const entries = normalized ?? this.normalizeCheckInEntries(details);
    const activeCompany = entries.companyEntries
      .filter(entry => !entry?.isDeleted)
      .map(entry => this.cleanString(entry.entryId))
      .find((entryId): entryId is string => !!entryId);
    if (activeCompany) {
      return activeCompany;
    }

    const hasOwnedContent = entries.ownedEntries.some(entry => this.hasCheckInOwnedEntryContent(entry));
    if (hasOwnedContent) {
      return 'new';
    }

    const defaultOption = this.companyCheckInOptions.find(option => option?.id)?.id;
    return defaultOption || 'new';
  }

  protected determineCheckOutSelection(
    details: HospitalityDetails | undefined,
    normalized?: { ownedEntries: HospitalityCheckOutOwnedEntry[]; companyEntries: HospitalityCheckOutCompanyEntry[] }
  ): string {
    const entries = normalized ?? this.normalizeCheckOutEntries(details);
    const activeCompany = entries.companyEntries
      .filter(entry => !entry?.isDeleted)
      .map(entry => this.cleanString(entry.entryId))
      .find((entryId): entryId is string => !!entryId);
    if (activeCompany) {
      return activeCompany;
    }

    const hasOwnedContent = entries.ownedEntries.some(entry => this.hasCheckOutOwnedEntryContent(entry));
    if (hasOwnedContent) {
      return 'new';
    }

    const defaultOption = this.companyCheckOutOptions.find(option => option?.id)?.id;
    return defaultOption || 'new';
  }

  protected createHospitalityDetailsGroup(details?: HospitalityDetails): FormGroup {
    const checkInEntries = this.normalizeCheckInEntries(details);
    const primaryOwnedEntry = checkInEntries.ownedEntries.length
      ? checkInEntries.ownedEntries[0]
      : undefined;
    const checkInSelection = this.determineCheckInSelection(details, checkInEntries);
    const checkOutEntries = this.normalizeCheckOutEntries(details);
    const checkOutSelection = this.determineCheckOutSelection(details, checkOutEntries);
    const houseRuleEntries = this.normalizeHouseRuleEntries(details);
    const applianceGuideEntries = this.normalizeApplianceGuideEntries(details);
    const additionalInfoEntries = this.normalizeAdditionalInfoEntries(details);

    const hostContactGroup = this.fb.group({
      name: [details?.hostContact?.name || ''],
      phone: [details?.hostContact?.phone || '', [phoneValidator()]],
      email: [details?.hostContact?.email || ''],
      whatsapp: [details?.hostContact?.whatsapp || ''],
      supportHours: [''],
      supportHoursSlots: this.fb.array([]),
    });
    this.initializeHostSupportHourSlots(hostContactGroup, details?.hostContact?.supportHours || '');

    const group = this.fb.group({
      wifi: this.fb.group({
        networkName: [details?.wifi?.networkName || ''],
        password: [details?.wifi?.password || ''],
        instructions: [details?.wifi?.instructions || ''],
        supportContact: [details?.wifi?.supportContact || ''],
      }),
      hostContact: hostContactGroup,
      checkIn: this.fb.group({
        selectedEntryId: [checkInSelection],
        ownedEntry: this.createCheckInOwnedEntryGroup(primaryOwnedEntry),
      }),
      checkOut: this.fb.group({
        selectedCompanyId: [checkOutSelection],
        ownedEntries: this.fb.array(
          checkOutEntries.ownedEntries.length
            ? checkOutEntries.ownedEntries.map(entry => this.createCheckOutOwnedEntryGroup(entry))
            : [this.createCheckOutOwnedEntryGroup()]
        ),
      }),
      houseRules: this.fb.group({
        ownedEntries: this.fb.array(
          houseRuleEntries.ownedEntries.length
            ? houseRuleEntries.ownedEntries.map(entry => this.createHouseRuleOwnedEntryGroup(entry))
            : [this.createHouseRuleOwnedEntryGroup()]
        ),
        selectedCompanyIds: this.fb.array(
          houseRuleEntries.selectedCompanyIds.map(id => this.fb.control(id))
        ),
      }),
      applianceGuides: this.fb.group({
        ownedEntries: this.fb.array(
          applianceGuideEntries.ownedEntries.length
            ? applianceGuideEntries.ownedEntries.map(entry => this.createApplianceGuideOwnedEntryGroup(entry))
            : [this.createApplianceGuideOwnedEntryGroup()]
        ),
        selectedCompanyIds: this.fb.array(
          applianceGuideEntries.selectedCompanyIds.map(id => this.fb.control(id))
        ),
      }),
      additionalInfo: this.fb.group({
        infoSections: this.fb.array(
          additionalInfoEntries.ownedEntries.length
            ? additionalInfoEntries.ownedEntries.map(entry => this.createInfoSectionGroup(entry))
            : [this.createInfoSectionGroup()]
        ),
        selectedCompanyIds: this.fb.array(
          additionalInfoEntries.selectedCompanyIds.map(id => this.fb.control(id))
        ),
      }),
      localTips: this.fb.group({
        intro: [details?.localTips?.intro || ''],
        highlights: this.fb.array(
          (details?.localTips?.highlights || []).map(link => this.createMapHighlightGroup(link))
        ),
      }),
    });

    this.ensureHospitalityArrayDefaults(group);
    this.setOwnedEntryPreviewOpen(group, 'houseRules', true);
    this.setOwnedEntryPreviewOpen(group, 'checkOut', checkOutSelection === 'new');
    this.setOwnedEntryPreviewOpen(group, 'additionalInfo', true);
    this.setAllApplianceGuidePreviewState(group, true);
    return group;
  }

  protected getHospitalityDetailsForm(form: FormGroup): FormGroup {
    return this.resolveHospitalityGroup(form);
  }

  protected getHouseRulesGroup(form: FormGroup): FormGroup {
    return this.getHospitalityDetailsForm(form).get('houseRules') as FormGroup;
  }

  getHouseRuleOwnedEntries(form: FormGroup): FormArray {
    return this.getHouseRulesGroup(form).get('ownedEntries') as FormArray;
  }

  addHouseRuleOwnedEntry(form: FormGroup): void {
    this.getHouseRuleOwnedEntries(form).push(this.createHouseRuleOwnedEntryGroup());
  }

  removeHouseRuleOwnedEntry(form: FormGroup, index: number): void {
    this.getHouseRuleOwnedEntries(form).removeAt(index);
  }

  protected getHouseRuleOwnedEntry(form: FormGroup, index: number): FormGroup {
    return this.getHouseRuleOwnedEntries(form).at(index) as FormGroup;
  }

  getHouseRuleOwnedEntryLinks(form: FormGroup, index: number): FormArray {
    return this.getHouseRuleOwnedEntry(form, index).get('links') as FormArray;
  }

  addHouseRuleOwnedEntryLink(form: FormGroup, index: number): void {
    this.getHouseRuleOwnedEntryLinks(form, index).push(this.createLinkGroup());
  }

  removeHouseRuleOwnedEntryLink(form: FormGroup, entryIndex: number, linkIndex: number): void {
    this.getHouseRuleOwnedEntryLinks(form, entryIndex).removeAt(linkIndex);
  }

  protected getHouseRuleSelectedCompanyIdsArray(form: FormGroup): FormArray {
    return this.getHouseRulesGroup(form).get('selectedCompanyIds') as FormArray;
  }

  getHouseRuleSelectedCompanyIds(form: FormGroup): string[] {
    const array = this.getHouseRuleSelectedCompanyIdsArray(form);
    if (!array) {
      return [];
    }
    return array.controls
      .map(control => control.value)
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
  }

  isHouseRuleCompanySelected(form: FormGroup, companyId: string): boolean {
    return this.getHouseRuleSelectedCompanyIds(form).includes(companyId);
  }

  protected findHouseRuleCompanyInsertIndex(array: FormArray, companyId: string): number {
    const targetIndex = this.companyHouseRuleOptions.findIndex((option: CompanyAdditionalInfoOption) => option.id === companyId);
    if (targetIndex === -1) {
      return array.length;
    }
    for (let index = 0; index < array.length; index += 1) {
      const value = array.at(index)?.value;
      const currentIndex = this.companyHouseRuleOptions.findIndex((option: CompanyAdditionalInfoOption) => option.id === value);
      if (currentIndex === -1) {
        continue;
      }
      if (currentIndex > targetIndex) {
        return index;
      }
    }
    return array.length;
  }

  setHouseRuleCompanySelection(form: FormGroup, companyId: string, selected: boolean): void {
    const array = this.getHouseRuleSelectedCompanyIdsArray(form);
    if (!array) {
      return;
    }
    const values = this.getHouseRuleSelectedCompanyIds(form);
    const existingIndex = values.indexOf(companyId);
    if (selected) {
      if (existingIndex !== -1) {
        return;
      }
      const insertIndex = this.findHouseRuleCompanyInsertIndex(array, companyId);
      array.insert(insertIndex, this.fb.control(companyId));
      return;
    }
    if (existingIndex === -1) {
      return;
    }
    array.removeAt(existingIndex);
    this.ensureCompanyPreviewState(form).houseRules.delete(companyId);
  }

  protected getCheckInGroup(form: FormGroup): FormGroup {
    return this.getHospitalityDetailsForm(form).get('checkIn') as FormGroup;
  }

  protected getCheckInOwnedEntry(form: FormGroup): FormGroup {
    return this.getCheckInGroup(form).get('ownedEntry') as FormGroup;
  }

  protected getCheckInSelectionControl(form: FormGroup): FormControl | null {
    return this.getCheckInGroup(form).get('selectedEntryId') as FormControl | null;
  }

  protected getCheckInSelectionValue(form: FormGroup): string {
    const control = this.getCheckInSelectionControl(form);
    const value = control?.value;
    return typeof value === 'string' ? value : '';
  }

  getCheckInRadioOptions(form: FormGroup): CompanyCheckInOption[] {
    const selection = this.getCheckInSelectionValue(form);
    if (selection && selection !== 'new' && !this.companyCheckInOptions.some(option => option.id === selection)) {
      return [...this.companyCheckInOptions, { id: selection, title: selection }];
    }
    return this.companyCheckInOptions;
  }

  isCheckInNewSelected(form: FormGroup): boolean {
    return this.getCheckInSelectionValue(form) === 'new';
  }

  protected buildCheckInCompanyDisplayEntry(entry: HospitalityCompanyEntry): HospitalityCheckInOwnedEntry | undefined {
    if (!entry) {
      return undefined;
    }
    const entryId = this.cleanString(entry.entryId);
    const option = entryId ? this.getCompanyCheckInOption(entryId) : undefined;
    const overridesRecord = (entry?.overrides || {}) as Record<string, any>;
    const contentRecord = ((entry as any)?.content || {}) as Record<string, any>;
    const schedule = overridesRecord['schedule'] || contentRecord['schedule'] || option?.schedule;
    const links = this.mergeHospitalityLinks(overridesRecord['links'], contentRecord['links'], option?.links);
    const digitalKeys = this.mergeDigitalKeys(
      overridesRecord['digitalKeys'],
      contentRecord['digitalKeys'],
      option?.digitalKeys,
    );

    const combined: any = {
      title: this.pickFirstString(overridesRecord['title'], contentRecord['title'], option?.title, entryId),
      instructions: this.pickFirstString(overridesRecord['instructions'], contentRecord['instructions'], option?.instructions),
      keyPickupLocation: this.pickFirstString(
        overridesRecord['keyPickupLocation'],
        contentRecord['keyPickupLocation'],
        option?.keyPickupLocation,
      ),
      contact: this.pickFirstString(overridesRecord['contact'], contentRecord['contact'], option?.contact),
      schedule,
    };

    if (links.length) {
      combined.links = links;
    }
    if (digitalKeys.length) {
      combined.digitalKeys = digitalKeys;
    }

    return this.buildCheckInOwnedEntry(combined);
  }

  getCheckInEntriesForDisplay(details?: HospitalityDetails | null): HospitalityCheckInOwnedEntry[] {
    const entries = this.normalizeCheckInEntries(details ?? undefined).ownedEntries;
    if (!Array.isArray(entries)) {
      return [];
    }
    return entries.filter(entry => !entry?.isDeleted);
  }

  getCheckInCompanyEntriesForDisplay(details?: HospitalityDetails | null): HospitalityCheckInOwnedEntry[] {
    const entries = this.normalizeCheckInEntries(details ?? undefined).companyEntries;
    if (!Array.isArray(entries)) {
      return [];
    }
    return entries
      .filter(entry => !!entry && !entry.isDeleted)
      .map(entry => this.buildCheckInCompanyDisplayEntry(entry))
      .filter((entry): entry is HospitalityCheckInOwnedEntry => !!entry);
  }

  getCheckOutOwnedEntriesForDisplay(details?: HospitalityDetails | null): HospitalityCheckOutOwnedEntry[] {
    const { ownedEntries } = this.normalizeCheckOutEntries(details ?? undefined);
    if (!Array.isArray(ownedEntries)) {
      return [];
    }
    return ownedEntries.filter(entry => !entry?.isDeleted);
  }

  protected buildCheckOutCompanyDisplayEntry(entry: HospitalityCheckOutCompanyEntry): HospitalityCheckOutDisplayEntry | undefined {
    if (!entry) {
      return undefined;
    }
    const entryId = this.cleanString(entry.entryId);
    const option = entryId ? this.getCompanyCheckOutOption(entryId) : undefined;
    const overridesRecord = (entry?.overrides || {}) as Record<string, any>;
    const contentRecord = ((entry as any)?.content || {}) as Record<string, any>;

    const baseLabel = this.pickFirstString(option?.label, entryId);
    const display: HospitalityCheckOutDisplayEntry = {
      title: baseLabel || '',
      sourceLabel: baseLabel,
    };

    const title = this.pickFirstString(overridesRecord['title'], contentRecord['title'], option?.title, display.title);
    if (title) {
      display.title = title;
    }

    const instructions = this.pickFirstString(overridesRecord['instructions'], contentRecord['instructions'], option?.instructions);
    if (instructions) {
      display.instructions = instructions;
    }

    const rawCheckOutTime = this.pickFirstString(overridesRecord['checkOutTime'], contentRecord['checkOutTime'], option?.checkOutTime);
    const normalizedCheckOutTime = this.normalizeTimeValue(rawCheckOutTime);
    if (normalizedCheckOutTime) {
      display.checkOutTime = normalizedCheckOutTime;
    } else if (rawCheckOutTime) {
      display.checkOutTime = rawCheckOutTime;
    }

    const dropOffLocation = this.pickFirstString(
      overridesRecord['dropOffLocation'],
      contentRecord['dropOffLocation'],
      option?.dropOffLocation,
    );
    if (dropOffLocation) {
      display.dropOffLocation = dropOffLocation;
    }

    const contact = this.pickFirstString(overridesRecord['contact'], contentRecord['contact'], option?.contact);
    if (contact) {
      display.contact = contact;
    }

    const links = this.mergeHospitalityLinks(overridesRecord['links'], contentRecord['links'], option?.links);
    if (links.length) {
      display.links = links;
    }

    const checklist = this.normalizeChecklist(overridesRecord['checklist'])
      || this.normalizeChecklist(contentRecord['checklist'])
      || this.normalizeChecklist(option?.checklist);
    if (checklist) {
      display.checklist = checklist;
    }

    return this.hasCheckOutOwnedEntryContent(display) ? display : undefined;
  }

  getCheckOutCompanyEntriesForDisplay(details?: HospitalityDetails | null): HospitalityCheckOutDisplayEntry[] {
    const { companyEntries } = this.normalizeCheckOutEntries(details ?? undefined);
    if (!Array.isArray(companyEntries)) {
      return [];
    }
    return companyEntries
      .filter(entry => !!entry && !entry.isDeleted)
      .map(entry => this.buildCheckOutCompanyDisplayEntry(entry))
      .filter((entry): entry is HospitalityCheckOutDisplayEntry => !!entry);
  }

  getHouseRuleEntriesForDisplay(details?: HospitalityDetails | null): HospitalityHouseRuleOwnedEntry[] {
    const houseRules = details?.houseRules;
    if (!houseRules) {
      return [];
    }

    const sanitizedEntries: HospitalityHouseRuleOwnedEntry[] = [];

    const pushSanitizedEntry = (entry?: Partial<HospitalityHouseRuleOwnedEntry> | null): void => {
      if (!entry || (entry as HospitalityHouseRuleOwnedEntry)?.isDeleted) {
        return;
      }
      const normalized = this.normalizeHouseRuleOwnedEntry(entry);
      const rule = this.cleanString(normalized.rule);
      if (!rule) {
        return;
      }
      const sanitized: HospitalityHouseRuleOwnedEntry = {
        ...normalized,
        rule,
      } as HospitalityHouseRuleOwnedEntry;
      if (Array.isArray(normalized.links) && normalized.links.length) {
        const links = normalized.links
          .map(link => this.cleanLink(link))
          .filter((link: HospitalityLink | undefined | null): link is HospitalityLink => !!link);
        if (links.length) {
          sanitized.links = links;
        } else {
          delete sanitized.links;
        }
      } else {
        delete sanitized.links;
      }
      sanitizedEntries.push(sanitized);
    };

    if (this.isHouseRulesSectionPayload(houseRules)) {
      const ownedEntries = Array.isArray(houseRules.ownedEntries)
        ? houseRules.ownedEntries
        : [];
      ownedEntries.forEach(entry => pushSanitizedEntry(entry));
      return this.sortEntriesByOrder(sanitizedEntries);
    }

    const legacyEntries = this.normalizeLegacyHouseRuleOwnedEntries(houseRules as LegacyHospitalityHouseRules);
    legacyEntries.forEach(entry => pushSanitizedEntry(entry));

    return this.sortEntriesByOrder(sanitizedEntries);
  }

  getPrimaryCheckInPreview(details?: HospitalityDetails | null): HospitalityCheckInOwnedEntry | undefined {
    const ownedEntries = this.getCheckInEntriesForDisplay(details);
    if (ownedEntries.length) {
      return ownedEntries[0];
    }
    const companyEntries = this.getCheckInCompanyEntriesForDisplay(details);
    return companyEntries.length ? companyEntries[0] : undefined;
  }

  getPrimaryCheckOutPreview(details?: HospitalityDetails | null): HospitalityCheckOutOwnedEntry | undefined {
    const ownedEntries = this.getCheckOutOwnedEntriesForDisplay(details);
    if (ownedEntries.length) {
      return ownedEntries[0];
    }
    const companyEntries = this.getCheckOutCompanyEntriesForDisplay(details);
    return companyEntries.length ? companyEntries[0] : undefined;
  }

  getPrimaryHouseRulePreview(details?: HospitalityDetails | null): string | undefined {
    const ownedEntries = this.getHouseRuleEntriesForDisplay(details);
    if (ownedEntries.length) {
      return ownedEntries[0].rule;
    }
    const companyEntries = this.getHouseRuleCompanyEntriesForDisplay(details);
    if (companyEntries.length) {
      return companyEntries[0].rule;
    }
    return undefined;
  }

  getHouseRuleCompanyEntriesForDisplay(details?: HospitalityDetails | null): HospitalityHouseRuleOwnedEntry[] {
    const normalized = this.normalizeHouseRuleEntries(details ?? undefined);
    const selectedIds = normalized.selectedCompanyIds;
    if (!selectedIds.length) {
      return [];
    }

    const houseRules = details?.houseRules;
    const entryMap = new Map<string, HospitalityCompanyEntry>();
    if (this.isHouseRulesSectionPayload(houseRules)) {
      const companyEntries = Array.isArray(houseRules.companyEntries) ? houseRules.companyEntries : [];
      companyEntries.forEach(entry => {
        const entryId = this.cleanString(entry?.entryId);
        if (!entryId || entry?.isDeleted || entryMap.has(entryId)) {
          return;
        }
        entryMap.set(entryId, entry as HospitalityCompanyEntry);
      });
    }

    return selectedIds
      .map((id, index) => {
        const entry = entryMap.get(id);
        const option = this.getCompanyHouseRuleOption(id);
        const overridesRecord = (entry?.overrides || {}) as Record<string, any>;
        const contentRecord = ((entry as any)?.content || {}) as Record<string, any>;
        const ruleValue = this.pickFirstString(
          overridesRecord['rule'],
          overridesRecord['title'],
          overridesRecord['summary'],
          overridesRecord['details'],
          contentRecord['rule'],
          contentRecord['title'],
          contentRecord['summary'],
          contentRecord['details'],
          option?.label,
          id,
        ) || id;
        const links = this.mergeHospitalityLinks(
          overridesRecord['links'],
          contentRecord['links'],
          option?.links,
        );
        const display: HospitalityHouseRuleOwnedEntry = {
          rule: ruleValue,
          order: index,
        } as HospitalityHouseRuleOwnedEntry;
        if (links.length) {
          display.links = links;
        }
        return display;
      })
      .filter((entry): entry is HospitalityHouseRuleOwnedEntry => !!entry && !!this.cleanString(entry.rule));
  }

  getApplianceGuideOwnedEntriesForDisplay(details?: HospitalityDetails | null): HospitalityApplianceGuide[] {
    const applianceGuides = details?.applianceGuides;
    const entries: HospitalityApplianceGuide[] = [];

    const pushEntry = (
      appliance?: string,
      summary?: string,
      stepsInput?: Array<string | null | undefined> | null,
      resources?: HospitalityLink[],
      detailsText?: string,
    ) => {
      const sanitizedResources = this.mergeHospitalityLinks(
        resources as Array<HospitalityLink | null | undefined> | undefined,
      );
      const applianceValue = this.cleanString(appliance);
      const summaryValue = this.cleanString(summary);
      const normalizedSteps = this.normalizeApplianceGuideSteps(stepsInput, detailsText);
      const resolvedSummary = summaryValue || (normalizedSteps.length ? normalizedSteps[0] : undefined);
      if (applianceValue || resolvedSummary || normalizedSteps.length || sanitizedResources.length) {
        const view: HospitalityApplianceGuide = {
          appliance: applianceValue,
          summary: resolvedSummary,
        };
        if (normalizedSteps.length) {
          view.steps = normalizedSteps;
        }
        if (sanitizedResources.length) {
          view.resources = sanitizedResources;
        }
        entries.push(view);
      }
    };

    if (this.isApplianceGuidesSectionPayload(applianceGuides)) {
      const ownedEntries = Array.isArray(applianceGuides.ownedEntries) ? applianceGuides.ownedEntries : [];
      ownedEntries.forEach(entry => {
        if (entry?.isDeleted) {
          return;
        }
        const resources = this.mergeHospitalityLinks(
          entry?.resources as Array<HospitalityLink | null | undefined> | undefined,
          (entry as any)?.links as Array<HospitalityLink | null | undefined> | undefined,
        );
        pushEntry(entry?.appliance, entry?.summary, entry?.steps, resources);
      });
    } else if (Array.isArray(applianceGuides)) {
      this.normalizeLegacyApplianceGuideOwnedEntries(applianceGuides).forEach(entry => {
        const resources = this.mergeHospitalityLinks(
          entry.resources as Array<HospitalityLink | null | undefined> | undefined,
        );
        pushEntry(entry.appliance, entry.summary, entry.steps, resources);
      });
    }

    return entries;
  }

  getApplianceGuideCompanyEntriesForDisplay(details?: HospitalityDetails | null): HospitalityApplianceGuide[] {
    const applianceGuides = details?.applianceGuides;
    const entries: HospitalityApplianceGuide[] = [];

    const pushEntry = (
      appliance?: string,
      summary?: string,
      stepsInput?: Array<string | null | undefined> | null,
      resources?: HospitalityLink[],
      detailsText?: string,
    ) => {
      const applianceValue = this.cleanString(appliance);
      const summaryValue = this.cleanString(summary);
      const normalizedSteps = this.normalizeApplianceGuideSteps(stepsInput, detailsText);
      const normalizedResources = this.mergeHospitalityLinks(
        resources as Array<HospitalityLink | null | undefined> | undefined,
      );
      const resolvedSummary = summaryValue || (normalizedSteps.length ? normalizedSteps[0] : undefined);
      if (
        applianceValue
        || resolvedSummary
        || normalizedSteps.length
        || normalizedResources.length
      ) {
        const view: HospitalityApplianceGuide = {
          appliance: applianceValue,
          summary: resolvedSummary,
        };
        if (normalizedSteps.length) {
          view.steps = normalizedSteps;
        }
        if (normalizedResources.length) {
          view.resources = normalizedResources;
        }
        entries.push(view);
      }
    };

    const normalized = this.normalizeApplianceGuideEntries(details || undefined);
    const selectedIds = normalized.selectedCompanyIds;

    if (this.isApplianceGuidesSectionPayload(applianceGuides)) {
      const companyEntries = Array.isArray(applianceGuides.companyEntries) ? applianceGuides.companyEntries : [];
      const entryMap = new Map<string, HospitalityApplianceGuideCompanyEntry>();
      companyEntries.forEach(entry => {
        const entryId = this.cleanString(entry?.entryId);
        if (!entryId || entry?.isDeleted || entryMap.has(entryId)) {
          return;
        }
        entryMap.set(entryId, entry as HospitalityApplianceGuideCompanyEntry);
      });

      selectedIds.forEach(id => {
        const entry = entryMap.get(id);
        const option = this.getCompanyApplianceGuideOption(id);
        if (entry) {
          const content = entry?.content && typeof entry.content === 'object' ? entry.content : {};
          const overrides = entry?.overrides || {};
          const appliance = this.cleanString((content as any).appliance)
            || this.cleanString((overrides as HospitalityApplianceGuideCompanyEntryOverrides)?.appliance)
            || this.cleanString((overrides as any)?.title)
            || this.cleanString(option?.appliance)
            || id;
          const summary = this.cleanString((content as any).summary)
            || this.cleanString((overrides as any).summary)
            || this.cleanString(option?.summary)
            || appliance;
          const detailsText = this.cleanString((content as any).details)
            || this.cleanString((overrides as any).details);
          const contentSteps = Array.isArray((content as any).steps) ? (content as any).steps : undefined;
          const combinedResources = this.mergeHospitalityLinks(
            (overrides as HospitalityApplianceGuideCompanyEntryOverrides | undefined)?.resources as Array<HospitalityLink | null | undefined> | undefined,
            (overrides as any)?.links as Array<HospitalityLink | null | undefined> | undefined,
            (content as HospitalityApplianceGuideCompanyEntryContent | undefined)?.resources as Array<HospitalityLink | null | undefined> | undefined,
            (content as any)?.links as Array<HospitalityLink | null | undefined> | undefined,
            option?.resources as Array<HospitalityLink | null | undefined> | undefined,
          );
          pushEntry(appliance, summary, contentSteps || option?.steps || null, combinedResources, detailsText);
          return;
        }

        if (option) {
          const appliance = this.cleanString(option.appliance) || id;
          const summary = this.cleanString(option.summary) || appliance;
          const resources = this.mergeHospitalityLinks(
            option.resources as Array<HospitalityLink | null | undefined> | undefined,
          );
          pushEntry(appliance, summary, option.steps, resources);
          return;
        }

        pushEntry(id, undefined, undefined, undefined);
      });
    } else {
      selectedIds.forEach(id => {
        const option = this.getCompanyApplianceGuideOption(id);
        const appliance = this.cleanString(option?.appliance) || id;
        const summary = this.cleanString(option?.summary) || appliance;
        const resources = this.mergeHospitalityLinks(
          option?.resources as Array<HospitalityLink | null | undefined> | undefined,
        );
        pushEntry(appliance, summary, option?.steps, resources);
      });
    }

    return entries;
  }

  getAdditionalInfoOwnedEntriesForDisplay(details?: HospitalityDetails | null): HospitalityAdditionalInfoDisplayEntry[] {
    const normalized = this.normalizeAdditionalInfoEntries(details ?? undefined);
    const ownedEntries = Array.isArray(normalized.ownedEntries) ? normalized.ownedEntries : [];
    return ownedEntries
      .filter(entry => !!entry && !entry.isDeleted)
      .map(entry => {
        const title = this.cleanString(entry.title);
        const body = this.cleanString(entry.body);
        const display: HospitalityAdditionalInfoDisplayEntry = {
          title: title || '',
          body: body || '',
        } as HospitalityAdditionalInfoDisplayEntry;
        if (Array.isArray(entry.links) && entry.links.length) {
          display.links = entry.links;
        }
        return display;
      })
      .filter(entry =>
        this.cleanString(entry.title)
        || this.cleanString(entry.body)
        || this.hasLinksContent(entry.links)
      );
  }

  getAdditionalInfoCompanyEntriesForDisplay(details?: HospitalityDetails | null): HospitalityAdditionalInfoDisplayEntry[] {
    const normalized = this.normalizeAdditionalInfoEntries(details ?? undefined);
    const selectedIds = normalized.selectedCompanyIds;
    if (!selectedIds.length) {
      return [];
    }

    const additionalInfo = details?.additionalInfo;
    const entryMap = new Map<string, HospitalityCompanyEntry>();
    if (this.isAdditionalInfoSectionPayload(additionalInfo)) {
      const companyEntries = Array.isArray(additionalInfo.companyEntries) ? additionalInfo.companyEntries : [];
      companyEntries.forEach(entry => {
        const entryId = this.cleanString(entry?.entryId);
        if (!entryId || entry?.isDeleted || entryMap.has(entryId)) {
          return;
        }
        entryMap.set(entryId, entry as HospitalityCompanyEntry);
      });
    }

    return selectedIds
      .map((id, index) => {
        const entry = entryMap.get(id);
        const option = this.getCompanyAdditionalInfoOption(id);
        const overridesRecord = (entry?.overrides || {}) as Record<string, any>;
        const contentRecord = ((entry as any)?.content || {}) as Record<string, any>;
        const title = this.pickFirstString(overridesRecord['title'], contentRecord['title'], option?.title, id);
        const summary = this.pickFirstString(overridesRecord['summary'], contentRecord['summary'], option?.summary);
        const body = this.pickFirstString(
          overridesRecord['details'],
          overridesRecord['body'],
          contentRecord['details'],
          contentRecord['body'],
          option?.body,
        );
        const links = this.mergeHospitalityLinks(overridesRecord['links'], contentRecord['links'], option?.links);
        const display: HospitalityAdditionalInfoDisplayEntry = {
          title: title || summary || id,
          body: body || '',
          summary: summary || undefined,
          order: index,
        } as HospitalityAdditionalInfoDisplayEntry;
        if (links.length) {
          display.links = links;
        }
        return display;
      })
      .filter(entry =>
        this.cleanString(entry.title)
        || this.cleanString(entry.body)
        || this.cleanString(entry.summary)
        || this.hasLinksContent(entry.links)
      );
  }

  getCheckInLinks(form: FormGroup): FormArray {
    return this.getCheckInOwnedEntry(form).get('links') as FormArray;
  }

  addCheckInLink(form: FormGroup): void {
    this.getCheckInLinks(form).push(this.createLinkGroup());
  }

  removeCheckInLink(form: FormGroup, linkIndex: number): void {
    this.getCheckInLinks(form).removeAt(linkIndex);
  }

  getCheckInDigitalKeys(form: FormGroup): FormArray {
    return this.getCheckInOwnedEntry(form).get('digitalKeys') as FormArray;
  }

  addCheckInDigitalKey(form: FormGroup): void {
    this.getCheckInDigitalKeys(form).push(this.createDigitalKeyGroup());
  }

  removeCheckInDigitalKey(form: FormGroup, keyIndex: number): void {
    this.getCheckInDigitalKeys(form).removeAt(keyIndex);
  }

  protected getCheckOutGroup(form: FormGroup): FormGroup {
    return this.getHospitalityDetailsForm(form).get('checkOut') as FormGroup;
  }

  getCheckOutSelectionControl(form: FormGroup): FormControl {
    const control = this.getCheckOutGroup(form)?.get('selectedCompanyId');
    if (!control) {
      throw new Error('Check-out selection control is missing from the hospitality form.');
    }
    return control as FormControl;
  }

  protected getCheckOutSelectionValue(form: FormGroup): string {
    const control = this.getCheckOutSelectionControl(form);
    const value = control.value;
    return typeof value === 'string' ? value : '';
  }

  selectCheckOutOption(form: FormGroup, hospitalityGroup: FormGroup, optionId: string): void {
    const control = this.getCheckOutSelectionControl(form);
    if (control.value !== optionId) {
      control.setValue(optionId);
    }
    this.setOwnedEntryPreviewOpen(hospitalityGroup, 'checkOut', optionId === 'new');
  }

  isCheckOutNewSelected(form: FormGroup): boolean {
    return this.getCheckOutSelectionValue(form) === 'new';
  }

  isCheckOutOwnedPreviewOpen(group: FormGroup): boolean {
    return this.isOwnedEntryPreviewOpen(group, 'checkOut');
  }

  getCheckOutOwnedEntries(form: FormGroup): FormArray {
    return this.getCheckOutGroup(form).get('ownedEntries') as FormArray;
  }

  protected getCheckOutOwnedEntry(form: FormGroup, index: number): FormGroup {
    return this.getCheckOutOwnedEntries(form).at(index) as FormGroup;
  }

  getCheckOutOwnedEntryLinks(form: FormGroup, entryIndex: number): FormArray {
    return this.getCheckOutOwnedEntry(form, entryIndex).get('links') as FormArray;
  }

  addCheckOutOwnedEntryLink(form: FormGroup, entryIndex: number): void {
    this.getCheckOutOwnedEntryLinks(form, entryIndex).push(this.createLinkGroup());
  }

  removeCheckOutOwnedEntryLink(form: FormGroup, entryIndex: number, linkIndex: number): void {
    this.getCheckOutOwnedEntryLinks(form, entryIndex).removeAt(linkIndex);
  }

  protected getCheckOutOwnedEntryChecklist(form: FormGroup, entryIndex: number): FormGroup {
    return this.getCheckOutOwnedEntry(form, entryIndex).get('checklist') as FormGroup;
  }

  getCheckOutOwnedEntryChecklistItems(form: FormGroup, entryIndex: number): FormArray {
    return this.getCheckOutOwnedEntryChecklist(form, entryIndex).get('items') as FormArray;
  }

  addCheckOutOwnedEntryChecklistItem(form: FormGroup, entryIndex: number): void {
    this.getCheckOutOwnedEntryChecklistItems(form, entryIndex).push(
      this.fb.control('', [Validators.maxLength(this.HOSPITALITY_LIMITS.checklistItem)]),
    );
  }

  removeCheckOutOwnedEntryChecklistItem(form: FormGroup, entryIndex: number, itemIndex: number): void {
    this.getCheckOutOwnedEntryChecklistItems(form, entryIndex).removeAt(itemIndex);
  }

  protected getApplianceGuidesGroup(form: FormGroup): FormGroup {
    return this.getHospitalityDetailsForm(form).get('applianceGuides') as FormGroup;
  }

  getApplianceGuideOwnedEntries(form: FormGroup): FormArray {
    return this.getApplianceGuidesGroup(form).get('ownedEntries') as FormArray;
  }

  addApplianceGuideOwnedEntry(form: FormGroup): void {
    const entries = this.getApplianceGuideOwnedEntries(form);
    entries.push(this.createApplianceGuideOwnedEntryGroup());
    const hospitalityGroup = this.getHospitalityDetailsForm(form);
    this.setOwnedEntryPreviewOpen(hospitalityGroup, 'applianceGuides', true, entries.length - 1);
  }

  removeApplianceGuideOwnedEntry(form: FormGroup, index: number): void {
    this.getApplianceGuideOwnedEntries(form).removeAt(index);
    this.shiftApplianceGuidePreviewState(this.getHospitalityDetailsForm(form), index);
  }

  protected getApplianceGuideOwnedEntry(form: FormGroup, index: number): FormGroup {
    return this.getApplianceGuideOwnedEntries(form).at(index) as FormGroup;
  }

  getApplianceGuideOwnedEntrySteps(form: FormGroup, index: number): FormArray {
    return this.getApplianceGuideOwnedEntry(form, index).get('steps') as FormArray;
  }

  addApplianceGuideOwnedEntryStep(form: FormGroup, entryIndex: number): void {
    this.getApplianceGuideOwnedEntrySteps(form, entryIndex).push(
      this.fb.control('', [Validators.maxLength(this.HOSPITALITY_LIMITS.details)]),
    );
  }

  removeApplianceGuideOwnedEntryStep(form: FormGroup, entryIndex: number, stepIndex: number): void {
    this.getApplianceGuideOwnedEntrySteps(form, entryIndex).removeAt(stepIndex);
  }

  getApplianceGuideOwnedEntryResources(form: FormGroup, index: number): FormArray {
    return this.getApplianceGuideOwnedEntry(form, index).get('resources') as FormArray;
  }

  addApplianceGuideOwnedEntryResource(form: FormGroup, entryIndex: number): void {
    this.getApplianceGuideOwnedEntryResources(form, entryIndex).push(this.createLinkGroup());
  }

  removeApplianceGuideOwnedEntryResource(form: FormGroup, entryIndex: number, resourceIndex: number): void {
    this.getApplianceGuideOwnedEntryResources(form, entryIndex).removeAt(resourceIndex);
  }

  protected getApplianceGuideSelectedCompanyIdsArray(form: FormGroup): FormArray {
    return this.getApplianceGuidesGroup(form).get('selectedCompanyIds') as FormArray;
  }

  getApplianceGuideSelectedCompanyIds(form: FormGroup): string[] {
    const array = this.getApplianceGuideSelectedCompanyIdsArray(form);
    if (!array) {
      return [];
    }
    return array.controls
      .map(control => control.value)
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
  }

  isApplianceGuideCompanySelected(form: FormGroup, companyId: string): boolean {
    return this.getApplianceGuideSelectedCompanyIds(form).includes(companyId);
  }

  protected findApplianceGuideCompanyInsertIndex(array: FormArray, companyId: string): number {
    const targetIndex = this.companyApplianceGuideOptions.findIndex((option: CompanyAdditionalInfoOption) => option.id === companyId);
    if (targetIndex === -1) {
      return array.length;
    }
    for (let index = 0; index < array.length; index += 1) {
      const value = array.at(index)?.value;
      const currentIndex = this.companyApplianceGuideOptions.findIndex((option: CompanyAdditionalInfoOption) => option.id === value);
      if (currentIndex === -1) {
        continue;
      }
      if (currentIndex > targetIndex) {
        return index;
      }
    }
    return array.length;
  }

  setApplianceGuideCompanySelection(form: FormGroup, companyId: string, selected: boolean): void {
    const array = this.getApplianceGuideSelectedCompanyIdsArray(form);
    if (!array) {
      return;
    }
    const values = this.getApplianceGuideSelectedCompanyIds(form);
    const existingIndex = values.indexOf(companyId);
    if (selected) {
      if (existingIndex !== -1) {
        return;
      }
      const insertIndex = this.findApplianceGuideCompanyInsertIndex(array, companyId);
      array.insert(insertIndex, this.fb.control(companyId));
      return;
    }
    if (existingIndex === -1) {
      return;
    }
    array.removeAt(existingIndex);
    this.ensureCompanyPreviewState(form).applianceGuides.delete(companyId);
  }

  protected getLocalTipsForm(form: FormGroup): FormGroup {
    return this.getHospitalityDetailsForm(form).get('localTips') as FormGroup;
  }

  getLocalTipsHighlights(form: FormGroup): FormArray {
    return this.getLocalTipsForm(form).get('highlights') as FormArray;
  }

  addLocalTipHighlight(form: FormGroup): void {
    this.getLocalTipsHighlights(form).push(this.createMapHighlightGroup());
  }

  removeLocalTipHighlight(form: FormGroup, index: number): void {
    this.getLocalTipsHighlights(form).removeAt(index);
  }

  protected getAdditionalInfoGroup(form: FormGroup): FormGroup {
    return this.getHospitalityDetailsForm(form).get('additionalInfo') as FormGroup;
  }

  protected getAdditionalInfoSelectedCompanyIdsArray(form: FormGroup): FormArray {
    return this.getAdditionalInfoGroup(form).get('selectedCompanyIds') as FormArray;
  }

  getAdditionalInfoSelectedCompanyIds(form: FormGroup): string[] {
    const array = this.getAdditionalInfoSelectedCompanyIdsArray(form);
    if (!array) {
      return [];
    }
    return array.controls
      .map(control => control.value)
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
  }

  isAdditionalInfoCompanySelected(form: FormGroup, companyId: string): boolean {
    return this.getAdditionalInfoSelectedCompanyIds(form).includes(companyId);
  }

  protected findAdditionalInfoCompanyInsertIndex(array: FormArray, companyId: string): number {
    const targetIndex = this.companyAdditionalInfoOptions.findIndex((option: CompanyAdditionalInfoOption) => option.id === companyId);
    if (targetIndex === -1) {
      return array.length;
    }
    for (let index = 0; index < array.length; index += 1) {
      const value = array.at(index)?.value;
      const currentIndex = this.companyAdditionalInfoOptions.findIndex((option: CompanyAdditionalInfoOption) => option.id === value);
      if (currentIndex === -1) {
        continue;
      }
      if (currentIndex > targetIndex) {
        return index;
      }
    }
    return array.length;
  }

  setAdditionalInfoCompanySelection(form: FormGroup, companyId: string, selected: boolean): void {
    const array = this.getAdditionalInfoSelectedCompanyIdsArray(form);
    if (!array) {
      return;
    }
    const values = this.getAdditionalInfoSelectedCompanyIds(form);
    const existingIndex = values.indexOf(companyId);
    if (selected) {
      if (existingIndex !== -1) {
        return;
      }
      const insertIndex = this.findAdditionalInfoCompanyInsertIndex(array, companyId);
      array.insert(insertIndex, this.fb.control(companyId));
      return;
    }
    if (existingIndex === -1) {
      return;
    }
    array.removeAt(existingIndex);
    this.ensureCompanyPreviewState(form).additionalInfo.delete(companyId);
  }

  getInfoSections(form: FormGroup): FormArray {
    return this.getAdditionalInfoGroup(form).get('infoSections') as FormArray;
  }

  addInfoSection(form: FormGroup): void {
    this.getInfoSections(form).push(this.createInfoSectionGroup());
  }

  removeInfoSection(form: FormGroup, index: number): void {
    this.getInfoSections(form).removeAt(index);
  }

  getInfoSectionLinks(section: AbstractControl): FormArray {
    return (section as FormGroup).get('links') as FormArray;
  }

  addInfoSectionLink(section: AbstractControl): void {
    this.getInfoSectionLinks(section).push(this.createLinkGroup());
  }

  removeInfoSectionLink(section: AbstractControl, index: number): void {
    this.getInfoSectionLinks(section).removeAt(index);
  }

  getCompanyCheckOutOption(id: string): CompanyCheckOutOption | undefined {
    return this.companyCheckOutOptions.find((option: CompanyCheckOutOption) => option.id === id);
  }

  getCompanyCheckInOption(id: string): CompanyCheckInOption | undefined {
    return this.companyCheckInOptions.find((option: CompanyCheckInOption) => option.id === id);
  }

  getCompanyHouseRuleOption(id: string): CompanyHouseRuleOption | undefined {
    return this.companyHouseRuleOptions.find((option: CompanyHouseRuleOption) => option.id === id);
  }

  getCompanyApplianceGuideOption(id: string): CompanyApplianceGuideOption | undefined {
    return this.companyApplianceGuideOptions.find((option: CompanyApplianceGuideOption) => option.id === id);
  }

  getCompanyAdditionalInfoOption(id: string): CompanyAdditionalInfoOption | undefined {
    return this.companyAdditionalInfoOptions.find((option: CompanyAdditionalInfoOption) => option.id === id);
  }

  protected normalizeAddressInput(value?: string | null): string | undefined {
    return normalizeMapAddressInput(value);
  }

  protected toValidAddress(value?: string | null): string | undefined {
    return toValidMapAddress(value);
  }

  protected cleanString(value?: unknown): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const candidate = typeof value === 'string'
      ? value
      : typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : undefined;

    if (candidate === undefined) {
      return undefined;
    }

    const trimmed = candidate.trim();
    return trimmed.length ? trimmed : undefined;
  }

  protected cleanLink(link?: HospitalityLink): HospitalityLink | undefined {
    if (!link) {
      return undefined;
    }
    const cleaned: HospitalityLink = {};
    const label = this.cleanString(link.label);
    const url = this.cleanString(link.url);
    const address = normalizeMapAddressInput(link.address) || this.cleanString(link.address);
    if (label) {
      cleaned.label = label;
    }
    if (url) {
      cleaned.url = url;
    }
    if (address) {
      cleaned.address = address;
    }
    const coordinates = this.normalizeMapCoordinates(link.coordinates);
    if (coordinates) {
      cleaned.coordinates = coordinates;
    }
    const document = this.normalizeLinkDocument(link.document);
    if (document) {
      cleaned.document = document;
    }
    return Object.keys(cleaned).length ? cleaned : undefined;
  }

  protected normalizeLinkDocument(document?: HospitalityLinkDocument | null): HospitalityLinkDocument | undefined {
    if (!document || typeof document !== 'object') {
      return undefined;
    }
    const fileName = this.cleanString(document.fileName);
    const url = this.cleanString(document.url);
    const fileType = this.cleanString(document.fileType);
    const previewUrl = this.cleanString(document.previewUrl);
    const thumbnailUrl = this.cleanString(document.thumbnailUrl);
    if (!url && !fileName && !fileType && !previewUrl && !thumbnailUrl) {
      return undefined;
    }
    const normalized: HospitalityLinkDocument = {};
    if (fileName) {
      normalized.fileName = fileName;
    }
    if (url) {
      normalized.url = url;
    }
    if (fileType) {
      normalized.fileType = fileType;
    }
    if (previewUrl) {
      normalized.previewUrl = previewUrl;
    }
    if (thumbnailUrl) {
      normalized.thumbnailUrl = thumbnailUrl;
    }
    return Object.keys(normalized).length ? normalized : undefined;
  }

  protected normalizeDigitalKeyAccessType(value: unknown): HospitalityDigitalKeyAccessType | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const normalized = value.trim().toLowerCase();
    return this.digitalKeyAccessTypes.find(type => type === normalized) as HospitalityDigitalKeyAccessType | undefined;
  }

  protected normalizeDigitalKeyInput(
    key?: HospitalityDigitalKey | HospitalityLink | null,
  ): HospitalityDigitalKey | undefined {
    if (!key || typeof key !== 'object') {
      return undefined;
    }

    if ('accessType' in key) {
      const candidate = key as HospitalityDigitalKey;
      const accessType = this.normalizeDigitalKeyAccessType(candidate.accessType);
      if (!accessType) {
        return undefined;
      }
      const normalized: HospitalityDigitalKey = { accessType };
      const code = this.cleanString(candidate.code);
      const link = this.cleanString(candidate.link);
      const notes = this.cleanString(candidate.notes);
      if (code) { normalized.code = code; }
      if (link) { normalized.link = link; }
      if (notes) { normalized.notes = notes; }
      return normalized;
    }

    const linkCandidate = key as HospitalityLink;
    const label = this.cleanString(linkCandidate.label);
    const url = this.cleanString(linkCandidate.url);

    if (url) {
      const normalized: HospitalityDigitalKey = { accessType: 'app', link: url };
      if (label) {
        normalized.notes = label;
      }
      return normalized;
    }

    if (label) {
      return { accessType: 'other', notes: label };
    }

    return undefined;
  }

  protected cleanDigitalKey(key?: HospitalityDigitalKey | HospitalityLink | null): HospitalityDigitalKey | undefined {
    const normalized = this.normalizeDigitalKeyInput(key);
    if (!normalized) {
      return undefined;
    }

    if (normalized.accessType === 'pin') {
      return normalized.code
        ? { accessType: 'pin', code: normalized.code }
        : undefined;
    }

    if (normalized.accessType === 'app') {
      if (!normalized.link) {
        return undefined;
      }
      const cleaned: HospitalityDigitalKey = { accessType: 'app', link: normalized.link };
      if (normalized.notes) {
        cleaned.notes = normalized.notes;
      }
      return cleaned;
    }

    if (!normalized.notes) {
      return undefined;
    }
    const cleaned: HospitalityDigitalKey = { accessType: 'other', notes: normalized.notes };
    if (normalized.link) {
      cleaned.link = normalized.link;
    }
    return cleaned;
  }

  protected hasDigitalKeyContent(keys?: HospitalityDigitalKey[]): boolean {
    if (!Array.isArray(keys)) {
      return false;
    }
    return keys.some(key => !!this.cleanDigitalKey(key));
  }

  protected mergeDigitalKeys(
    ...keySets: Array<HospitalityDigitalKey[] | null | undefined>
  ): HospitalityDigitalKey[] {
    return keySets
      .filter((keys): keys is HospitalityDigitalKey[] => Array.isArray(keys))
      .reduce((acc, keys) => acc.concat(keys), [] as HospitalityDigitalKey[])
      .map(key => this.cleanDigitalKey(key))
      .filter((key): key is HospitalityDigitalKey => !!key);
  }

  protected formatCoordinateInput(value?: number | string | null): string {
    if (value === undefined || value === null) {
      return '';
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    return Number.isNaN(value) ? '' : `${value}`;
  }

  protected normalizeMapCoordinates(value?: HospitalityLink['coordinates']): MapCoordinates | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    const lat = toValidLatitude((value as any).lat);
    const lng = toValidLongitude((value as any).lng);
    if (lat === undefined && lng === undefined) {
      return undefined;
    }
    if (lat === undefined || lng === undefined) {
      return undefined;
    }
    return { lat, lng };
  }

  protected sortEntriesByOrder<T extends { order?: number }>(entries: T[]): T[] {
    return entries
      .slice()
      .sort((a, b) => {
        const orderA = typeof a?.order === 'number' && Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = typeof b?.order === 'number' && Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
  }

  protected pickFirstString(...values: Array<string | null | undefined>): string | undefined {
    for (const value of values) {
      const normalized = this.cleanString(value);
      if (normalized) {
        return normalized;
      }
    }
    return undefined;
  }

  protected mergeHospitalityLinks(
    ...linkSets: Array<Array<HospitalityLink | null | undefined> | null | undefined>
  ): HospitalityLink[] {
    return linkSets
      .filter((links): links is Array<HospitalityLink | null | undefined> => Array.isArray(links))
      .flat()
      .map(link => this.cleanLink(link || undefined))
      .filter((link: HospitalityLink | undefined | null): link is HospitalityLink => !!link);
  }

  protected normalizeChecklist(raw: any): HospitalityChecklist | undefined {
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }
    const title = this.cleanString(raw?.title);
    const items = Array.isArray(raw?.items)
      ? raw.items
        .map((item: string | null | undefined) => this.cleanString(item))
        .filter((item: string | undefined): item is string => !!item)
      : [];
    const checklist: HospitalityChecklist = {};
    if (title) {
      checklist.title = title;
    }
    if (items.length) {
      checklist.items = items;
    }
    return Object.keys(checklist).length ? checklist : undefined;
  }

  protected buildCompanyEntries<T extends HospitalityCompanyEntryBase = HospitalityCompanyEntry>(
    values: Array<string | { entryId?: string | null; order?: number | null } | null | undefined> | undefined,
  ): T[] {
    if (!Array.isArray(values)) {
      return [];
    }
    return values
      .map((value, index) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const entryId = this.cleanString(value.entryId);
          if (!entryId) {
            return undefined;
          }
          const orderValue = value.order;
          const order = typeof orderValue === 'number' && Number.isFinite(orderValue)
            ? orderValue
            : index;
          return { entryId, order } as T;
        }
        const entryId = this.cleanString(value as string | null | undefined);
        if (!entryId) {
          return undefined;
        }
        return { entryId, order: index } as T;
      })
      .filter((entry): entry is T => !!entry);
  }

  protected buildCheckInOwnedEntry(rawEntry: any): HospitalityCheckInOwnedEntry | undefined {
    if (!rawEntry || typeof rawEntry !== 'object') {
      return undefined;
    }

    const entry: HospitalityCheckInOwnedEntry = {} as HospitalityCheckInOwnedEntry;
    let hasValue = false;

    const title = this.cleanString(rawEntry?.title);
    if (title) {
      entry.title = title;
      hasValue = true;
    }

    const scheduleFrom = this.normalizeTimeValue(rawEntry?.schedule?.from);
    const scheduleUntil = this.normalizeTimeValue(rawEntry?.schedule?.until);
    if (scheduleFrom || scheduleUntil) {
      entry.schedule = {};
      if (scheduleFrom) { entry.schedule.from = scheduleFrom; }
      if (scheduleUntil) { entry.schedule.until = scheduleUntil; }
      hasValue = true;
    }

    const instructions = this.cleanString(rawEntry?.instructions);
    if (instructions) {
      entry.instructions = instructions;
      hasValue = true;
    }

    const keyPickupLocation = this.toValidAddress(rawEntry?.keyPickupLocation ?? rawEntry?.location);
    if (keyPickupLocation) {
      entry.keyPickupLocation = keyPickupLocation;
      hasValue = true;
    }

    const contact = this.cleanString(rawEntry?.contact);
    if (contact) {
      entry.contact = contact;
      hasValue = true;
    }

    const links = (rawEntry?.links || [])
      .map((link: HospitalityLink | undefined) => this.cleanLink(link))
      .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link);
    if (links.length) {
      entry.links = links;
      hasValue = true;
    }

    const digitalKeys = (rawEntry?.digitalKeys || [])
      .map((key: HospitalityDigitalKey | HospitalityLink | undefined) => this.cleanDigitalKey(key))
      .filter((key: HospitalityDigitalKey | undefined): key is HospitalityDigitalKey => !!key);
    if (digitalKeys.length) {
      entry.digitalKeys = digitalKeys;
      hasValue = true;
    }

    if (!entry.title && hasValue) {
      entry.title =
        instructions
        || keyPickupLocation
        || contact
        || (scheduleFrom || scheduleUntil ? `Check-in ${scheduleFrom || '—'} – ${scheduleUntil || '—'}` : undefined)
        || 'Check-in information';
    }

    return hasValue ? entry : undefined;
  }

  protected buildSectionPayload<TOwned, TCompany extends HospitalityCompanyEntryBase>(
    ownedEntries: TOwned[],
    companyEntries: TCompany[],
    companyEntryIds?: Array<string | { entryId?: string | null; order?: number | null }>,
  ): HospitalitySectionPayload<TOwned, TCompany> | undefined {
    const hasCompanyIds = Array.isArray(companyEntryIds) && companyEntryIds.length > 0;
    if (!ownedEntries.length && !companyEntries.length && !hasCompanyIds) {
      return undefined;
    }

    const payload: HospitalitySectionPayload<TOwned, TCompany> = {
      ownedEntries,
      companyEntries,
    };

    if (!companyEntries.length) {
      delete (payload as Partial<HospitalitySectionPayload<TOwned, TCompany>>).companyEntries;
    }

    if (hasCompanyIds) {
      payload.companyEntryIds = companyEntryIds;
    }

    return payload;
  }

  protected extractHospitalityDetails(form: FormGroup): HospitalityDetailsPayload | undefined {
    const detailsGroup = this.getHospitalityDetailsForm(form);
    const raw = detailsGroup.getRawValue() as any;
    const details: HospitalityDetailsPayload = {};

    if (raw.wifi) {
      const wifi: NonNullable<HospitalityDetailsPayload['wifi']> = {};
      const networkName = this.cleanString(raw.wifi.networkName);
      const password = this.cleanString(raw.wifi.password);
      const instructions = this.cleanString(raw.wifi.instructions);
      const supportContact = this.cleanString(raw.wifi.supportContact);
      if (networkName) { wifi.networkName = networkName; }
      if (password) { wifi.password = password; }
      if (instructions) { wifi.instructions = instructions; }
      if (supportContact) { wifi.supportContact = supportContact; }
      if (Object.keys(wifi).length) {
        details.wifi = wifi;
      }
    }

    if (raw.hostContact) {
      const hostContact: NonNullable<HospitalityDetailsPayload['hostContact']> = {};
      const name = this.cleanString(raw.hostContact.name);
      const phone = this.cleanString(raw.hostContact.phone);
      const email = this.cleanString(raw.hostContact.email);
      const whatsapp = this.cleanString(raw.hostContact.whatsapp);
      const supportHours = this.cleanString(raw.hostContact.supportHours);
      if (name) { hostContact.name = name; }
      if (phone) { hostContact.phone = phone; }
      if (email) { hostContact.email = email; }
      if (whatsapp) { hostContact.whatsapp = whatsapp; }
      if (supportHours) { hostContact.supportHours = supportHours; }
      if (Object.keys(hostContact).length) {
        details.hostContact = hostContact;
      }
    }

    const rawCheckIn = raw.checkIn;
    if (rawCheckIn && typeof rawCheckIn === 'object') {
      const selectionRaw = rawCheckIn.selectedEntryId;
      const selectionValue = this.cleanString(selectionRaw);
      const ownedEntryRaw = rawCheckIn.ownedEntry;

      const ownedEntries: HospitalityCheckInOwnedEntry[] = [];
      const companyEntryIds: Array<{ entryId: string; order: number }> = [];

      if (!selectionValue || selectionValue === 'new') {
        const ownedEntry = this.buildCheckInOwnedEntry(ownedEntryRaw);
        if (ownedEntry) {
          ownedEntries.push(ownedEntry);
        }
      } else {
        companyEntryIds.push({ entryId: selectionValue, order: 0 });
      }

      const checkInSection = this.buildSectionPayload<HospitalityCheckInOwnedEntry, HospitalityCompanyEntry>(
        ownedEntries,
        [] as HospitalityCompanyEntry[],
        companyEntryIds
      );
      if (checkInSection) {
        details.checkIn = checkInSection;
      }
    }

    const rawCheckOut = raw.checkOut;
    if (rawCheckOut && typeof rawCheckOut === 'object') {
      const selectionRaw = rawCheckOut.selectedCompanyId;
      const selectionValue = this.cleanString(selectionRaw);
      const ownedEntries: HospitalityCheckOutOwnedEntry[] = [];
      const companyEntryIds: Array<{ entryId: string; order: number }> = [];

      if (!selectionValue || selectionValue === 'new') {
        const ownedRaw = Array.isArray(rawCheckOut.ownedEntries) ? rawCheckOut.ownedEntries : [];
        ownedRaw.forEach((rawEntry: any) => {
          const entry: HospitalityCheckOutOwnedEntry = {} as HospitalityCheckOutOwnedEntry;
          let hasValue = false;

          const title = this.cleanString(rawEntry?.title);
          if (title) {
            entry.title = title;
            hasValue = true;
          }

          const instructions = this.cleanString(rawEntry?.instructions);
          if (instructions) {
            entry.instructions = instructions;
            hasValue = true;
          }

          const checkOutTime = this.normalizeTimeValue(rawEntry?.checkOutTime ?? rawEntry?.deadline);
          if (checkOutTime) {
            entry.checkOutTime = checkOutTime;
            hasValue = true;
          }

          const dropOffLocation = this.toValidAddress(rawEntry?.dropOffLocation ?? rawEntry?.location);
          if (dropOffLocation) {
            entry.dropOffLocation = dropOffLocation;
            hasValue = true;
          }

          const contact = this.cleanString(rawEntry?.contact);
          if (contact) {
            entry.contact = contact;
            hasValue = true;
          }

          const links = (rawEntry?.links || [])
            .map((link: HospitalityLink | undefined) => this.cleanLink(link))
            .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link);
          if (links.length) {
            entry.links = links;
            hasValue = true;
          }

          const checklistTitle = this.cleanString(rawEntry?.checklist?.title);
          const checklistItems = ((rawEntry?.checklist?.items) || [])
            .map((item: string | undefined) => this.cleanString(item) || '')
            .map((item: string) => item.trim())
            .filter((item: string) => item.length);
          if (checklistTitle || checklistItems.length) {
            entry.checklist = {};
            if (checklistTitle) {
              entry.checklist.title = checklistTitle;
            }
            if (checklistItems.length) {
              entry.checklist.items = checklistItems;
            }
            hasValue = true;
          }

          if (hasValue) {
            if (!entry.title) {
              entry.title =
                instructions
                || checkOutTime
                || dropOffLocation
                || contact
                || 'Check-out information';
            }
            ownedEntries.push(entry);
          }
        });
      }

      if (selectionValue && selectionValue !== 'new') {
        companyEntryIds.push({ entryId: selectionValue, order: 0 });
      }

      const checkOutSection = this.buildSectionPayload(
        ownedEntries,
        [] as HospitalityCheckOutCompanyEntry[],
        companyEntryIds,
      );
      if (checkOutSection) {
        details.checkOut = checkOutSection;
      }
    }

    const rawHouseRules = raw.houseRules;
    if (rawHouseRules && typeof rawHouseRules === 'object') {
      const houseRulesOwned: HospitalityHouseRuleOwnedEntry[] = [];
      const ownedRaw = Array.isArray(rawHouseRules.ownedEntries) ? rawHouseRules.ownedEntries : [];
      ownedRaw.forEach((rawEntry: any) => {
        const entry: HospitalityHouseRuleOwnedEntry = {} as HospitalityHouseRuleOwnedEntry;
        let hasValue = false;

        const rule = this.cleanString(rawEntry?.rule)
          || this.cleanString(rawEntry?.title)
          || this.cleanString(rawEntry?.summary)
          || this.cleanString(rawEntry?.details);
        if (rule) {
          entry.rule = rule;
          hasValue = true;
        }

        const links = (rawEntry?.links || [])
          .map((link: HospitalityLink | undefined) => this.cleanLink(link))
          .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link);
        if (links.length) {
          entry.links = links;
          hasValue = true;
        }

        if (hasValue) {
          houseRulesOwned.push(entry);
        }
      });

      const selectedIdsRaw = Array.isArray(rawHouseRules.selectedCompanyIds) ? rawHouseRules.selectedCompanyIds : [];
      const companyEntryIds: Array<{ entryId: string; order: number }> = [];
      selectedIdsRaw.forEach((value: string | { entryId?: string | null } | null | undefined, index: number) => {
        const entryId = this.cleanString(
          typeof value === 'string' || typeof value === 'number'
            ? value.toString()
            : value?.entryId
        );
        if (!entryId) {
          return;
        }
        companyEntryIds.push({ entryId, order: index });
      });

      const houseRulesSection = this.buildSectionPayload(
        houseRulesOwned,
        [] as HospitalityCompanyEntry[],
        companyEntryIds,
      );
      if (houseRulesSection) {
        details.houseRules = houseRulesSection;
      }
    }

    const applianceOwned: HospitalityApplianceGuideOwnedEntry[] = [];
    const companyEntryIds: Array<{ entryId: string; order: number }> = [];
    const rawApplianceGuides = raw.applianceGuides;
    if (rawApplianceGuides) {
      const ownedRaw = Array.isArray(rawApplianceGuides.ownedEntries) ? rawApplianceGuides.ownedEntries : [];
      ownedRaw.forEach((rawEntry: any) => {
        const entry: HospitalityApplianceGuideOwnedEntry = { appliance: '', summary: '', steps: [] };
        let hasValue = false;

        const appliance = this.cleanString(rawEntry?.appliance);
        if (appliance) {
          entry.appliance = appliance;
          hasValue = true;
        }

        const summary = this.cleanString(rawEntry?.summary);
        if (summary) {
          entry.summary = summary;
          hasValue = true;
        }

        const steps = this.normalizeApplianceGuideSteps(rawEntry?.steps, rawEntry?.details);
        if (steps.length) {
          entry.steps = steps;
          hasValue = true;
        }

        const resources = this.mergeHospitalityLinks(
          rawEntry?.resources as Array<HospitalityLink | null | undefined> | undefined,
          rawEntry?.links as Array<HospitalityLink | null | undefined> | undefined,
        );
        if (resources.length) {
          entry.resources = resources;
          hasValue = true;
        }

        if (hasValue && entry.appliance && entry.summary && entry.steps && entry.steps.length) {
          applianceOwned.push(entry);
        }
      });

      const selectedIdsRaw = Array.isArray(rawApplianceGuides.selectedCompanyIds)
        ? rawApplianceGuides.selectedCompanyIds
        : [];
      selectedIdsRaw.forEach((value: string | { entryId?: string | null } | null | undefined, index: number) => {
        const entryId = this.cleanString(
          typeof value === 'string' || typeof value === 'number'
            ? value.toString()
            : value?.entryId
        );
        if (!entryId) {
          return;
        }
        companyEntryIds.push({ entryId, order: index });
      });
    }

    const applianceSection = this.buildSectionPayload(
      applianceOwned,
      [] as HospitalityApplianceGuideCompanyEntry[],
      companyEntryIds,
    );
    if (applianceSection) {
      details.applianceGuides = applianceSection;
    }

    const rawAdditionalInfo = raw.additionalInfo;
    if (rawAdditionalInfo && typeof rawAdditionalInfo === 'object') {
      const additionalInfoOwned: HospitalityAdditionalInfoOwnedEntry[] = [];
      const ownedRaw = Array.isArray(rawAdditionalInfo.infoSections) ? rawAdditionalInfo.infoSections : [];
      ownedRaw.forEach((rawEntry: any) => {
        const entry: HospitalityAdditionalInfoOwnedEntry = {} as HospitalityAdditionalInfoOwnedEntry;
        let hasValue = false;

        const title = this.cleanString(rawEntry?.title);
        if (title) {
          entry.title = title;
          hasValue = true;
        }

        const bodyText = this.cleanString(rawEntry?.body ?? rawEntry?.details);
        if (bodyText) {
          entry.body = bodyText;
          hasValue = true;
        }

        const links = (rawEntry?.links || [])
          .map((link: HospitalityLink | undefined) => this.cleanLink(link))
          .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link);
        if (links.length) {
          entry.links = links;
          hasValue = true;
        }

        if (hasValue && entry.title && entry.body) {
          additionalInfoOwned.push(entry);
        }
      });

      const selectedIdsRaw = Array.isArray(rawAdditionalInfo.selectedCompanyIds)
        ? rawAdditionalInfo.selectedCompanyIds
        : [];
      const companyEntryIds: Array<{ entryId: string; order: number }> = [];
      selectedIdsRaw.forEach((value: string | { entryId?: string | null } | null | undefined, index: number) => {
        const entryId = this.cleanString(
          typeof value === 'string' || typeof value === 'number'
            ? value.toString()
            : value?.entryId
        );
        if (!entryId) {
          return;
        }
        companyEntryIds.push({ entryId, order: index });
      });

      const additionalInfoSection = this.buildSectionPayload(
        additionalInfoOwned,
        [] as HospitalityCompanyEntry[],
        companyEntryIds,
      );
      if (additionalInfoSection) {
        details.additionalInfo = additionalInfoSection;
      }
    } else if (Array.isArray(raw.infoSections) && raw.infoSections.length) {
      const additionalInfoOwned: HospitalityAdditionalInfoOwnedEntry[] = [];
      (raw.infoSections as Array<{ title?: string | null; body?: string | null; links?: Array<HospitalityLink | null | undefined> }>)
        .forEach(section => {
          const title = this.cleanString(section?.title ?? undefined);
          const body = this.cleanString(section?.body ?? undefined);
          if (!title || !body) {
            return;
          }
          const links = (section?.links || [])
            .map((link: HospitalityLink | undefined | null) => this.cleanLink(link || undefined))
            .filter((link: HospitalityLink | undefined | null): link is HospitalityLink => !!link);
          const entry: HospitalityAdditionalInfoOwnedEntry = {
            title,
            body,
          };
          if (links.length) {
            entry.links = links;
          }
          additionalInfoOwned.push(entry);
        });
      const additionalInfoSection = this.buildSectionPayload(additionalInfoOwned, []);
      if (additionalInfoSection) {
        details.additionalInfo = additionalInfoSection;
      }
    }

    if (raw.localTips) {
      const intro = this.cleanString(raw.localTips.intro);
      const highlights = (raw.localTips.highlights || [])
        .map((link: HospitalityLink | undefined) => this.cleanLink(link))
        .filter((link: HospitalityLink | undefined): link is HospitalityLink => !!link);
      if (intro || highlights.length) {
        details.localTips = {};
        if (intro) { details.localTips.intro = intro; }
        if (highlights.length) { details.localTips.highlights = highlights; }
      }
    }

    return Object.keys(details).length ? details : undefined;
  }
}
